import { useEffect, useState, useMemo } from "react";
import { puedeEditarModulo, esSoloLecturaClaro, esContratista, BannerSoloLectura } from "./permisos";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer } from "recharts";
import * as XLSX from "xlsx";

const C = {
  red:    "#E30613",
  navy:   "#0f172a",
  gray:   "#1e293b",
  slate:  "#64748b",
  light:  "#94a3b8",
  white:  "#ffffff",
};

const TIPOS_TRABAJO = [
  "MPI - Preventivo Integral",
  "MPF - Preventivo Fuentes Poder",
  "Segmentaciones",
  "Reingenierias",
  "Desmonte Nodos",
  "Busqueda Ruido / QoE",
  "Fugaz",
  "Preventivos Fibra Optica",
  "Movimientos Infraestructura",
  "Vulnerabilidades (FotoReportes)",
  "Conciliacion - Cambio de Categoria",
  "Migracion de Red",
  "MPE - Preventivo en Edificios",
  "Meaco",
  "Otro"
];

const ESTADOS = ["Pendiente", "En proceso", "Completado", "Cancelado"];

const estadoColor = (estado) => {
  switch (estado) {
    case "Completado":  return { bg: "#dcfce7", color: "#15803d", border: "#86efac" };
    case "En proceso":  return { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" };
    case "Cancelado":   return { bg: "#fef2f2", color: "#dc2626", border: "#fca5a5" };
    default:            return { bg: "#fef9c3", color: "#92400e", border: "#fde68a" };
  }
};

const semaforo = (real, esperado) => {
  const diff = real - esperado;
  if (diff >= 0)   return "#15803d";
  if (diff >= -10) return "#ca8a04";
  return "#dc2626";
};

const formatFecha = (f) => {
  if (!f) return "—";
  return f.includes("T") ? f.split("T")[0] : f.substring(0, 10);
};

const formatFechaHora = (f) => {
  if (!f) return "—";
  try {
    const d = new Date(f);
    if (isNaN(d.getTime())) return f.substring(0, 10);
    const fecha = d.toISOString().split("T")[0];
    const horas = String(d.getHours()).padStart(2, "0");
    const minutos = String(d.getMinutes()).padStart(2, "0");
    return `${fecha} ${horas}:${minutos}`;
  } catch {
    return f.substring(0, 10);
  }
};

const formatMes = (f) => {
  if (!f) return "—";
  const d = f.includes("T") ? f.split("T")[0] : f.substring(0, 10);
  const [anio, mes] = d.split("-");
  return `${mes}-${anio}`;
};

export default function PlanesTrabajo({ usuario }) {
  const puedeEditar = puedeEditarModulo(usuario, "planes");
  const soloLectura = esSoloLecturaClaro(usuario) || esContratista(usuario);

  const [planes, setPlanes]             = useState([]);
  const [nodosDisponibles, setNodos]    = useState([]);
  const [vistaActual, setVista]         = useState("dashboard"); // lista | detalle | dashboard
  const [planSeleccionado, setPlan]     = useState(null);
  const [nodosDelPlan, setNodosDelPlan] = useState([]);
  const [avancesDelPlan, setAvances]    = useState([]);
  const [loading, setLoading]           = useState(false);

  // Filtros lista
  const [filtroTipo, setFiltroTipo]     = useState([]);   // multi-seleccion
  const [filtroEstado, setFiltroEstado] = useState([]);   // multi-seleccion
  const [filtroNombre, setFiltroNombre] = useState("");
  const [dropdownTipo, setDropdownTipo]     = useState(false);
  const [dropdownEstado, setDropdownEstado] = useState(false);

  // Buscador inverso por nodo
  const [busquedaNodoInverso, setBusquedaNodoInverso] = useState("");

  // Popup nodos de un plan (opción A)
  const [popupNodosPlan, setPopupNodosPlan] = useState(null); // { plan, nodos[] }
  const [loadingPopupNodos, setLoadingPopupNodos] = useState(false);

  // Tabla dinámica pivote
  const [pivotMes, setPivotMes]         = useState("");        // filtro mes para pivot
  const [pivotJefe, setPivotJefe]       = useState("");        // filtro activo por jefe
  const [pivotTipoW, setPivotTipoW]     = useState("");        // filtro activo por tipo

  // Modal plan
  const [showModalPlan, setShowModalPlan]   = useState(false);
  const [editandoPlan, setEditandoPlan]     = useState(null);
  const [formPlan, setFormPlan]             = useState({ tipo_trabajo: "", descripcion: "", fecha_inicio: "", fecha_fin: "", responsable: "", aliado: "", ciudad: "", estado_ot: "Pendiente", orden_trabajo: "", chg: "", estado: "Pendiente", avance_real: 0 });
  const [jefesDisponibles, setJefes]        = useState([]);
  const [aliadosDisponibles, setAliados]    = useState([]);
  const [ciudadesDisponibles, setCiudades]  = useState([]);

  // Selector multi-nodo en formulario
  const [nodosForm, setNodosForm]           = useState({}); // { id_nodo: { mes, categoria, fecha_corte } }
  const [busquedaNodoForm, setBusqNodoForm] = useState("");
  const [dropdownNodosForm, setDDNodosForm] = useState(false);
  const [mesesPorNodo, setMesesPorNodo]     = useState({}); // { id_nodo: [{mes, categoria, ultimo_corte_mes}] }

  // Modal nodos (detalle plan - agregar nodos post creacion)
  const [showModalNodos, setShowModalNodos] = useState(false);
  const [nodosSeleccionados, setNodosSelec] = useState([]);
  const [busquedaNodo, setBusquedaNodo]     = useState("");
  const [mesSeleccionado, setMesSelec]      = useState("");
  const [mesesDisponibles, setMeses]        = useState([]);
  const [filtroJefeNodo, setFiltroJefeNodo]     = useState("");
  const [filtroAliadoNodo, setFiltroAliadoNodo] = useState("");
  const [filtroCiudadNodo, setFiltroCiudadNodo] = useState("");
  const [filtroCategoriaNodo, setFiltroCategoriaNodo] = useState("");

  const [showModalAvance, setShowModalAvance] = useState(false);
  const [formAvance, setFormAvance] = useState({ avance_real: 0, observacion: "" });

  // Pestañas vista detalle
  const [tabDetalle, setTabDetalle]         = useState("nodos"); // "nodos" | "avances" | "efectividad"
  const [efectividad, setEfectividad]       = useState(null);

  // Sub-vista de Seguimiento
  const [subVistaSeg, setSubVistaSeg]       = useState("kanban"); // kanban | gantt | graficas
  const [loadingEfect, setLoadingEfect]     = useState(false);
  const [filtroEfect, setFiltroEfect]       = useState(""); // "" | "Efectivo" | "Parcial" | "Inefectivo"

  // Abre el modal de avance prellenando con el último avance registrado
  const abrirModalAvance = () => {
    const ultimoAvance = avancesDelPlan.length > 0
      ? parseFloat(avancesDelPlan[0].avance_real || 0)   // avancesDelPlan viene ordenado por fecha DESC
      : parseFloat(planSeleccionado?.avance_real || 0);
    setFormAvance({
      avance_real: ultimoAvance,
      observacion: ""
    });
    setShowModalAvance(true);
  };

  const API = process.env.REACT_APP_API_URL;

  const cargarPlanes = () => {
    const rol    = encodeURIComponent(usuario?.rol || "");
    const cedula = encodeURIComponent(usuario?.cedula || "");
    fetch(`${API}/planes?rol=${rol}&cedula=${cedula}`)
      .then(r => r.json())
      .then(d => setPlanes(d))
      .catch(console.error);
  };

  const cargarNodosDisponibles = () => {
    const rol    = encodeURIComponent(usuario?.rol || "");
    const cedula = encodeURIComponent(usuario?.cedula || "");
    // Traer TODOS los cortes disponibles para poder escoger el mes
    fetch(`${API}/nodos-todos-cortes?rol=${rol}&cedula=${cedula}`)
      .then(r => r.json())
      .then(d => {
        setNodos(d);
        // Extraer meses únicos disponibles
        const mesesUnicos = [...new Set(d.map(n => n.mes_anio))].sort((a, b) => b.localeCompare(a));
        setMeses(mesesUnicos);
        // Seleccionar el mes más reciente por defecto
        if (mesesUnicos.length > 0) setMesSelec(mesesUnicos[0]);
      })
      .catch(console.error);
  };

  // Cargar jefes, aliados y ciudades
  useEffect(() => {
    fetch(`${API}/jefes`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setJefes(d); })
      .catch(() => {});
    fetch(`${API}/aliados`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setAliados(d.map(a => a.nombre || a)); })
      .catch(() => {});
    fetch(`${API}/ciudades`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setCiudades(d); })
      .catch(() => {});
  }, [API]);

  // Cerrar dropdowns al hacer click fuera
  useEffect(() => {
    const handler = () => { setDropdownTipo(false); setDropdownEstado(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    cargarPlanes();
    cargarNodosDisponibles();
  }, [usuario]);

  const abrirDetalle = async (plan) => {
    setPlan(plan);
    setVista("detalle");
    setTabDetalle("nodos");
    setEfectividad(null);
    setFiltroEfect("");
    const [nodos, avances] = await Promise.all([
      fetch(`${API}/planes/${plan.id}/nodos`).then(r => r.json()),
      fetch(`${API}/planes/${plan.id}/avances`).then(r => r.json())
    ]);
    setNodosDelPlan(nodos);
    setAvances(avances);
  };

  const cargarEfectividad = async (planId) => {
    setLoadingEfect(true);
    try {
      const data = await fetch(`${API}/planes/${planId}/efectividad`).then(r => r.json());
      setEfectividad(data);
    } catch (e) {
      setEfectividad({ ok: false, mensaje: "Error al cargar efectividad." });
    }
    setLoadingEfect(false);
  };

  const abrirPopupNodos = async (plan) => {
    setLoadingPopupNodos(true);
    setPopupNodosPlan({ plan, nodos: [] });
    const nodos = await fetch(`${API}/planes/${plan.id}/nodos`).then(r => r.json());
    setPopupNodosPlan({ plan, nodos });
    setLoadingPopupNodos(false);
  };

  const guardarPlan = async () => {
    if (!formPlan.responsable) { alert("El Jefe Responsable es obligatorio."); return; }
    if (!formPlan.orden_trabajo?.trim()) { alert("La Orden de Trabajo (OT) es obligatoria."); return; }
    if (!formPlan.tipo_trabajo) { alert("El Tipo de Trabajo es obligatorio."); return; }

    setLoading(true);
    const url    = editandoPlan ? `${API}/planes/${editandoPlan.id}` : `${API}/planes`;
    const method = editandoPlan ? "PUT" : "POST";

    const body = {
      ...formPlan,
      creado_por: usuario?.usuario,
      rol: usuario?.rol
    };
    const res  = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const data = await res.json();
    if (data.ok) {
      // Si es creacion y hay nodos seleccionados, agregarlos
      if (!editandoPlan && Object.keys(nodosForm).length > 0) {
        const nodosArr = Object.entries(nodosForm).map(([id, info]) => ({
          nodo: id,
          fecha_corte: info.fecha_corte,
          m_fecha_mes: info.mes ? info.mes + "-01" : info.fecha_corte,
          categoria_inicio: info.categoria || null
        }));
        await fetch(`${API}/planes/${data.id}/nodos`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ nodos: nodosArr, rol: usuario?.rol })
        });
      }
      cargarPlanes();
      setShowModalPlan(false);
      setEditandoPlan(null);
      setNodosForm({});
    }
    setLoading(false);
  };
  // ── FUNCIONES SELECTOR MULTI-NODO EN FORMULARIO ─────────────────────────
  const cargarMesesNodo = async (idNodo) => {
    if (Array.isArray(mesesPorNodo[idNodo])) return; // ya cargado correctamente
    try {
      const data = await fetch(`${API}/nodo/${idNodo}/meses`).then(r => r.json());
      // Garantizar siempre array
      const lista = Array.isArray(data) ? data : [];
      setMesesPorNodo(prev => ({ ...prev, [idNodo]: lista }));
    } catch (e) {
      setMesesPorNodo(prev => ({ ...prev, [idNodo]: [] }));
    }
  };

  const toggleNodoForm = async (nodo) => {
    // nodo = { id_nodo, NOMBRE_NODO, categoria, fecha_corte, mes_anio }
    const id = nodo.id_nodo;
    if (nodosForm[id]) {
      // quitar
      const copia = { ...nodosForm };
      delete copia[id];
      setNodosForm(copia);
    } else {
      // agregar con mes más reciente por defecto
      await cargarMesesNodo(id);
      setNodosForm(prev => ({
        ...prev,
        [id]: {
          nombre: nodo.NOMBRE_NODO || id,
          mes: nodo.mes_anio || "",
          categoria: nodo.categoria || "",
          fecha_corte: formatFecha(nodo.fecha_corte)
        }
      }));
    }
  };

  const cambiarMesNodoForm = async (idNodo, nuevoMes) => {
    await cargarMesesNodo(idNodo);
    const meses = mesesPorNodo[idNodo] || [];
    const info = meses.find(m => m.mes === nuevoMes);
    setNodosForm(prev => ({
      ...prev,
      [idNodo]: {
        ...prev[idNodo],
        mes: nuevoMes,
        categoria: info?.categoria || prev[idNodo]?.categoria || "",
        fecha_corte: info?.ultimo_corte_mes ? formatFecha(info.ultimo_corte_mes) : prev[idNodo]?.fecha_corte
      }
    }));
  };

  const quitarNodoForm = (idNodo) => {
    const copia = { ...nodosForm };
    delete copia[idNodo];
    setNodosForm(copia);
  };



  const eliminarPlan = async (id) => {
    if (!window.confirm("¿Eliminar este plan? Se eliminarán también sus nodos y avances.")) return;
    await fetch(`${API}/planes/${id}?rol=${encodeURIComponent(usuario?.rol||"")}`, { method: "DELETE" });
    cargarPlanes();
  };

  const agregarNodos = async () => {
    if (nodosSeleccionados.length === 0) return;
    const nodos = nodosSeleccionados.map(item => ({
      nodo: item.id_nodo,
      fecha_corte: formatFecha(item.fecha_corte),
      m_fecha_mes: formatFecha(item.mes_anio)
    }));
    const res  = await fetch(`${API}/planes/${planSeleccionado.id}/nodos`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ nodos, rol: usuario?.rol }) });
    const data = await res.json();
    if (data.ok) {
      const nodosActualizados = await fetch(`${API}/planes/${planSeleccionado.id}/nodos`).then(r => r.json());
      setNodosDelPlan(nodosActualizados);
      setShowModalNodos(false);
      setNodosSelec([]);
    }
  };

  const quitarNodo = async (nodo, fecha_corte) => {
    if (!window.confirm("¿Quitar este nodo del plan?")) return;
    await fetch(`${API}/planes/${planSeleccionado.id}/nodos/${nodo}?fecha_corte=${fecha_corte}&rol=${encodeURIComponent(usuario?.rol||"")}`, { method: "DELETE" });
    setNodosDelPlan(prev => prev.filter(n => !(n.nodo === nodo && formatFecha(n.fecha_corte) === fecha_corte)));
  };

  const registrarAvance = async () => {
    setLoading(true);
    const avance_esperado = planSeleccionado.avance_esperado || 0;
    const avanceReal = parseFloat(formAvance.avance_real || 0);
    const ahora = new Date();
    const fecha_registro = `${ahora.getFullYear()}-${String(ahora.getMonth()+1).padStart(2,"0")}-${String(ahora.getDate()).padStart(2,"0")} ${String(ahora.getHours()).padStart(2,"0")}:${String(ahora.getMinutes()).padStart(2,"0")}:${String(ahora.getSeconds()).padStart(2,"0")}`;

    // Determinar nuevo estado automáticamente
    let nuevoEstado = planSeleccionado.estado;
    if (avanceReal >= 100 && nuevoEstado !== "Cancelado") {
      nuevoEstado = "Completado";
    } else if (avanceReal > 0 && nuevoEstado === "Pendiente") {
      nuevoEstado = "En proceso";
    }

    const res = await fetch(`${API}/planes/${planSeleccionado.id}/avances`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...formAvance, fecha_registro, avance_esperado, registrado_por: usuario?.usuario, rol: usuario?.rol })
    });
    const data = await res.json();
    if (data.ok) {
      // Si el estado cambió, actualizarlo en el backend
      if (nuevoEstado !== planSeleccionado.estado) {
        const p = planSeleccionado;
        const formatFechaSimple = (f) => f ? new Date(f).toISOString().slice(0, 10) : null;
        await fetch(`${API}/planes/${p.id}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nombre_plan:              p.nombre_plan,
            tipo_trabajo:             p.tipo_trabajo,
            descripcion:              p.descripcion || null,
            fecha_inicio:             formatFechaSimple(p.fecha_inicio),
            fecha_fin:                formatFechaSimple(p.fecha_fin),
            responsable:              p.responsable || null,
            orden_trabajo:            p.orden_trabajo || null,
            chg:                      p.chg || null,
            estado:                   nuevoEstado,
            avance_real:              avanceReal,
            rol:                      usuario?.rol
          })
        });
      }
      const [avances, planActualizado] = await Promise.all([
        fetch(`${API}/planes/${planSeleccionado.id}/avances`).then(r => r.json()),
        fetch(`${API}/planes/${planSeleccionado.id}`).then(r => r.json())
      ]);
      setAvances(avances);
      setPlan(planActualizado);
      setShowModalAvance(false);
      setFormAvance({ avance_real: 0, observacion: "" });
      cargarPlanes();
    }
    setLoading(false);
  };

  const [exportando, setExportando] = useState(false);

  const exportarExcel = async () => {
    setExportando(true);
    try {
      // 1. Hoja Planes
      const hojaPlanes = planes.map(p => ({
        "Plan":             p.nombre_plan,
        "Tipo Trabajo":     p.tipo_trabajo,
        "Responsable":      p.responsable || "",
        "OT":               p.orden_trabajo || "",
        "CHG":              p.chg || "",
        "Estado":           p.estado,
        "Avance Real (%)":  parseFloat(p.avance_real || 0).toFixed(1),
        "Avance Esperado (%)": parseFloat(p.avance_esperado || 0).toFixed(1),
        "Fecha Inicio":     formatFecha(p.fecha_inicio),
        "Fecha Fin":        formatFecha(p.fecha_fin),
        "Mes Ofensores":    p.mes_ofensores || "",
        "Total Nodos":      p.total_nodos || 0,
        "Total Avances":    p.total_avances || 0,
        "Descripcion":      p.descripcion || "",
      }));

      // 2. Hojas Nodos e Historial — traer datos de todos los planes en paralelo
      const resultados = await Promise.all(
        planes.map(p => Promise.all([
          fetch(`${API}/planes/${p.id}/nodos`).then(r => r.json()),
          fetch(`${API}/planes/${p.id}/avances`).then(r => r.json()),
        ]).then(([nodos, avances]) => ({ plan: p, nodos, avances })))
      );

      const hojaNodes = [];
      const hojaAvances = [];

      resultados.forEach(({ plan, nodos, avances }) => {
        nodos.forEach(n => {
          hojaNodes.push({
            "Plan":         plan.nombre_plan,
            "Tipo Trabajo": plan.tipo_trabajo,
            "OT":           plan.orden_trabajo || "",
            "CHG":          plan.chg || "",
            "Responsable":  plan.responsable || "",
            "Nodo":         n.nodo,
            "Nombre Nodo":  n.NOMBRE_NODO || "",
            "Ciudad":       n.ciudad || "",
            "Categoría":    n.categoria || "",
            "Prioridad":    n.tipo_prioridad || "",
            "Jefe Técnico": n.nombre_jefe || "",
            "Aliado":       n.aliado || "",
            "Causa Falla":  n.causa_falla || "",
            "Mes":          formatMes(n.fecha_corte),
          });
        });
        avances.forEach(a => {
          hojaAvances.push({
            "Plan":                plan.nombre_plan,
            "Tipo Trabajo":        plan.tipo_trabajo,
            "OT":                  plan.orden_trabajo || "",
            "CHG":                 plan.chg || "",
            "Responsable":         plan.responsable || "",
            "Fecha Registro":      formatFechaHora(a.fecha_registro),
            "Avance Real (%)":     parseFloat(a.avance_real || 0).toFixed(1),
            "Avance Esperado (%)": parseFloat(a.avance_esperado || 0).toFixed(1),
            "Observacion":         a.observacion || "",
            "Registrado Por":      a.registrado_por || "",
          });
        });
      });

      // 3. Construir workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(hojaPlanes),   "Planes");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(hojaNodes),    "Nodos");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(hojaAvances),  "Historial Avances");

      const fecha = new Date();
      const nombreArchivo = `Planes_Trabajo_${fecha.getFullYear()}${String(fecha.getMonth()+1).padStart(2,"0")}${String(fecha.getDate()).padStart(2,"0")}.xlsx`;
      XLSX.writeFile(wb, nombreArchivo);
    } catch (e) {
      console.error("Error exportando:", e);
      alert("Ocurrió un error al exportar. Intenta de nuevo.");
    }
    setExportando(false);
  };

  const toggleFiltro = (setter, valor) =>
    setter(prev => prev.includes(valor) ? prev.filter(v => v !== valor) : [...prev, valor]);

  const planesFiltrados = useMemo(() => planes.filter(p => {
    return (
      (filtroTipo.length   === 0 || filtroTipo.includes(p.tipo_trabajo)) &&
      (filtroEstado.length === 0 || filtroEstado.includes(p.estado)) &&
      (filtroNombre === "" || p.nombre_plan.toLowerCase().includes(filtroNombre.toLowerCase())) &&
      (!pivotJefe || (p.responsable || "Sin Jefe") === pivotJefe) &&
      (!pivotMes  || p.mes_ofensores === pivotMes)
    );
  }), [planes, filtroTipo, filtroEstado, filtroNombre, pivotJefe, pivotMes]);

  const nodosFiltrados = useMemo(() => {
    const yaAgregados = new Set(nodosDelPlan.map(n => `${n.nodo}||${formatFecha(n.fecha_corte)}`));
    return nodosDisponibles.filter(n => {
      const key = `${n.id_nodo}||${formatFecha(n.fecha_corte)}`;
      const coincideMes      = mesSeleccionado      === "" || n.mes_anio === mesSeleccionado;
      const coincideJefe     = filtroJefeNodo       === "" || (n.nombre_jefe||"") === filtroJefeNodo;
      const coincideAliado   = filtroAliadoNodo     === "" || (n.aliado||"") === filtroAliadoNodo;
      const coincideCiudad   = filtroCiudadNodo     === "" || (n.ciudad||"") === filtroCiudadNodo;
      const coincideCategoria= filtroCategoriaNodo  === "" || (n.categoria||"") === filtroCategoriaNodo;
      const coincideBusqueda = busquedaNodo === "" || n.id_nodo.toLowerCase().includes(busquedaNodo.toLowerCase()) || (n.NOMBRE_NODO||"").toLowerCase().includes(busquedaNodo.toLowerCase());
      return !yaAgregados.has(key) && coincideMes && coincideJefe && coincideAliado && coincideCiudad && coincideCategoria && coincideBusqueda;
    });
  }, [nodosDisponibles, nodosDelPlan, busquedaNodo, mesSeleccionado, filtroJefeNodo, filtroAliadoNodo, filtroCiudadNodo, filtroCategoriaNodo]);

  // Opciones únicas para filtros del modal
  const nodosDelMes = useMemo(() => mesSeleccionado === "" ? nodosDisponibles : nodosDisponibles.filter(n => n.mes_anio === mesSeleccionado), [nodosDisponibles, mesSeleccionado]);
  const optsJefeNodo    = useMemo(() => [...new Set(nodosDelMes.map(n => n.nombre_jefe).filter(Boolean))].sort(), [nodosDelMes]);
  const optsAliadoNodo  = useMemo(() => [...new Set(nodosDelMes.map(n => n.aliado).filter(Boolean))].sort(), [nodosDelMes]);
  const optsCiudadNodo  = useMemo(() => [...new Set(nodosDelMes.map(n => n.ciudad).filter(Boolean))].sort(), [nodosDelMes]);
  const optsCatNodo     = useMemo(() => [...new Set(nodosDelMes.map(n => n.categoria).filter(Boolean))].sort(), [nodosDelMes]);

  // ── TABLA DINÁMICA PIVOT ──────────────────────────────────────────
  // meses disponibles de planes
  const mesesPivot = useMemo(() => {
    const ms = [...new Set(planes.map(p => p.mes_ofensores).filter(Boolean))].sort((a,b) => b.localeCompare(a));
    return ms;
  }, [planes]);

  // planes filtrados por mes del pivot
  const planesParaPivot = useMemo(() => {
    if (!pivotMes) return planes;
    return planes.filter(p => p.mes_ofensores === pivotMes);
  }, [planes, pivotMes]);

  // jefes y tipos únicos en el pivot
  const pivotJefes = useMemo(() => [...new Set(planesParaPivot.map(p => p.responsable || "Sin Jefe"))].sort(), [planesParaPivot]);
  const pivotTipos = useMemo(() => [...new Set(planesParaPivot.map(p => p.tipo_trabajo).filter(Boolean))].sort(), [planesParaPivot]);

  // construir matriz pivot: { jefe: { tipo: count } }
  const pivotData = useMemo(() => {
    const matrix = {};
    pivotJefes.forEach(j => { matrix[j] = {}; pivotTipos.forEach(t => { matrix[j][t] = 0; }); matrix[j].__total__ = 0; });
    planesParaPivot.forEach(p => {
      const j = p.responsable || "Sin Jefe";
      const t = p.tipo_trabajo;
      if (matrix[j] && t) { matrix[j][t] = (matrix[j][t] || 0) + 1; matrix[j].__total__++; }
    });
    return matrix;
  }, [planesParaPivot, pivotJefes, pivotTipos]);

  // totales por tipo (fila de totales)
  const pivotTotalesTipo = useMemo(() => {
    const tot = {}; pivotTipos.forEach(t => { tot[t] = 0; });
    planesParaPivot.forEach(p => { if (p.tipo_trabajo) tot[p.tipo_trabajo] = (tot[p.tipo_trabajo] || 0) + 1; });
    return tot;
  }, [planesParaPivot, pivotTipos]);

  // Al hacer click en pivot, aplicar filtros a la tabla de abajo
  const aplicarFiltroPivot = (jefe, tipo) => {
    // Si ya está activo el mismo click, limpiar
    if (pivotJefe === jefe && pivotTipoW === tipo) {
      setPivotJefe(""); setPivotTipoW("");
      setFiltroTipo([]); setFiltroNombre("");
    } else {
      setPivotJefe(jefe); setPivotTipoW(tipo);
      if (tipo) setFiltroTipo([tipo]); else setFiltroTipo([]);
      if (jefe && jefe !== "Sin Jefe") setFiltroNombre(""); // filtrar por jefe en tabla
    }
  };

  const cardStyle  = { background: C.white, borderRadius: "12px", padding: "20px 24px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", marginBottom: "16px" };
  const thStyle    = { padding: "8px 10px", background: C.navy, color: "white", fontSize: "12px", fontWeight: "600", textAlign: "left", whiteSpace: "nowrap" };
  const tdStyle    = { padding: "7px 10px", fontSize: "12px", borderBottom: "1px solid #f1f5f9" };
  const inputStyle = { width: "100%", boxSizing: "border-box", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", color: C.navy, background: "#f8fafc", outline: "none" };
  const labelStyle = { display: "block", fontSize: "11px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px" };
  const btnRojo    = { background: `linear-gradient(135deg, ${C.red}, #B0000E)`, color: "white", border: "none", borderRadius: "8px", padding: "9px 18px", fontSize: "13px", fontWeight: "600", cursor: "pointer" };
  const btnGris    = { background: "#f1f5f9", color: C.navy, border: "1px solid #e2e8f0", borderRadius: "8px", padding: "9px 18px", fontSize: "13px", fontWeight: "600", cursor: "pointer" };

  // ── DASHBOARD ──
  const dashboardData = useMemo(() => {
    const porJefe = {};
    planes.forEach(p => {
      const jefe = p.responsable || "Sin Jefe";
      if (!porJefe[jefe]) porJefe[jefe] = { jefe, total: 0, completados: 0, enProceso: 0, pendientes: 0, cancelados: 0, avgAvanceReal: 0, avgAvanceEsperado: 0, totalNodos: 0, planesDetalle: [] };
      porJefe[jefe].total++;
      if (p.estado === "Completado") porJefe[jefe].completados++;
      if (p.estado === "En proceso") porJefe[jefe].enProceso++;
      if (p.estado === "Pendiente")  porJefe[jefe].pendientes++;
      if (p.estado === "Cancelado")  porJefe[jefe].cancelados++;
      porJefe[jefe].avgAvanceReal     += parseFloat(p.avance_real || 0);
      porJefe[jefe].avgAvanceEsperado += parseFloat(p.avance_esperado || 0);
      porJefe[jefe].totalNodos        += parseInt(p.total_nodos || 0);
      porJefe[jefe].planesDetalle.push(p);
    });
    return Object.values(porJefe).map(j => ({
      ...j,
      avgAvanceReal:     j.total > 0 ? Math.round(j.avgAvanceReal / j.total) : 0,
      avgAvanceEsperado: j.total > 0 ? Math.round(j.avgAvanceEsperado / j.total) : 0,
    }));
  }, [planes]);

  const [jefeExpandido, setJefeExpandido] = useState(null);
  const [semaforoSeleccion, setSemaforoSeleccion] = useState(null); // { jefe, mes, planes }
  const [semaforoFiltroEstado, setSemaforoFiltroEstado] = useState("all");

  // Filtros del dashboard (KPIs clickeables)
  const [dashFiltroEstado, setDashFiltroEstado] = useState(""); // "En proceso" | "Completado" | "Pendiente" | ""
  const [dashFiltroJefe,   setDashFiltroJefe]   = useState(""); // nombre del jefe

  const dashboardDataFiltrado = useMemo(() => {
    if (!dashFiltroEstado && !dashFiltroJefe) return dashboardData;
    return dashboardData
      .filter(row => !dashFiltroJefe || row.jefe === dashFiltroJefe)
      .map(row => ({
        ...row,
        planesDetalle: dashFiltroEstado
          ? row.planesDetalle.filter(p => p.estado === dashFiltroEstado)
          : row.planesDetalle,
      }))
      .filter(row => row.planesDetalle.length > 0);
  }, [dashboardData, dashFiltroEstado, dashFiltroJefe]);

  // Tabla dinámica: estados por mes de ofensores
  const pivotMesEstado = useMemo(() => {
    const meses = [...new Set(planes.map(p => p.mes_ofensores).filter(Boolean))].sort((a,b) => a.localeCompare(b));
    const estados = ["Pendiente", "En proceso", "Completado", "Cancelado"];
    const data = meses.map(mes => {
      const row = { mes };
      let total = 0;
      estados.forEach(est => {
        const count = planes.filter(p => p.mes_ofensores === mes && p.estado === est).length;
        row[est] = count;
        total += count;
      });
      row.total = total;
      return row;
    });
    // Fila totales
    const totales = { mes: "TOTAL" };
    estados.forEach(est => { totales[est] = planes.filter(p => p.estado === est).length; });
    totales.total = planes.length;
    return { data, estados, totales };
  }, [planes]);

  return (
    <div style={{ padding: "20px", fontFamily: "Arial" }}>
      <div>

      {/* TITULO + NAV */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ width: "4px", height: "24px", background: C.red, borderRadius: "2px" }} />
          <h2 style={{ margin: 0, color: C.navy, fontSize: "20px", fontWeight: "600" }}>Planes de Trabajo</h2>
          {soloLectura && <BannerSoloLectura />}
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {["lista", "dashboard", "seguimiento"].map(v => (
            <button key={v} onClick={() => setVista(v)} style={{
              ...btnGris,
              background: vistaActual === v ? C.navy : "#f1f5f9",
              color: vistaActual === v ? "white" : C.navy,
              textTransform: "capitalize"
            }}>{v === "lista" ? "Planes" : v === "dashboard" ? "Dashboard" : "Seguimiento"}</button>
          ))}
          {vistaActual === "dashboard" && (
            <button onClick={exportarExcel} disabled={exportando} style={{ ...btnGris, display: "flex", alignItems: "center", gap: "6px", opacity: exportando ? 0.7 : 1 }}>
              {exportando ? "⏳ Exportando..." : "⬇ Exportar Excel"}
            </button>
          )}
          {puedeEditar && vistaActual === "lista" && (
            <button onClick={() => { setEditandoPlan(null); setNodosForm({}); setFormPlan({ tipo_trabajo: "", descripcion: "", fecha_inicio: "", fecha_fin: "", responsable: "", aliado: "", ciudad: "", estado_ot: "Pendiente", orden_trabajo: "", chg: "", estado: "Pendiente", avance_real: 0 }); setShowModalPlan(true); }} style={btnRojo}>
              + Nuevo Plan
            </button>
          )}
          {vistaActual === "lista" && (
            <button onClick={exportarExcel} disabled={exportando} style={{ ...btnGris, display: "flex", alignItems: "center", gap: "6px", opacity: exportando ? 0.7 : 1 }}>
              {exportando ? "⏳ Exportando..." : "⬇ Exportar Excel"}
            </button>
          )}
        </div>
      </div>

      {/* ══ VISTA LISTA ══ */}
      {vistaActual === "lista" && (
        <>
          {/* ══ TABLA DINÁMICA PIVOTE ══ */}
          <div style={cardStyle}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
                <span style={{ fontSize: "13px", fontWeight: "700", color: C.navy, textTransform: "uppercase", letterSpacing: "0.05em" }}>Resumen por Jefe y Tipo de Trabajo</span>
                {(pivotJefe || pivotTipoW) && (
                  <span onClick={() => { setPivotJefe(""); setPivotTipoW(""); setFiltroTipo([]); }}
                    style={{ background: C.red, color: "white", borderRadius: "12px", padding: "1px 10px", fontSize: "11px", fontWeight: "600", cursor: "pointer", marginLeft: "6px" }}>
                    Filtro activo: {[pivotJefe, pivotTipoW].filter(Boolean).join(" · ")} ✕
                  </span>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <label style={{ fontSize: "11px", color: C.slate, fontWeight: "600" }}>Mes:</label>
                <select value={pivotMes} onChange={e => { setPivotMes(e.target.value); setPivotJefe(""); setPivotTipoW(""); setFiltroTipo([]); }}
                  style={{ padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "12px", cursor: "pointer", background: "white" }}>
                  <option value="">Todos los meses</option>
                  {mesesPivot.map((m, i) => (
                    <option key={i} value={m}>{new Date(m + "-01T12:00:00").toLocaleDateString("es-CO", { month: "short", year: "numeric" }).replace(".", "")}</option>
                  ))}
                </select>
              </div>
            </div>
            {pivotTipos.length === 0 ? (
              <div style={{ textAlign: "center", padding: "20px", color: C.slate, fontSize: "13px" }}>No hay planes para mostrar.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, background: "#1e3a5f", minWidth: "160px" }}>Responsable</th>
                      {pivotTipos.map((t, i) => (
                        <th key={i} style={{ ...thStyle, background: "#1e3a5f", textAlign: "center", minWidth: "100px", fontSize: "10px" }}>{t}</th>
                      ))}
                      <th style={{ ...thStyle, background: "#111827", textAlign: "center", minWidth: "70px" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pivotJefes.map((jefe, ji) => (
                      <tr key={ji} style={{ background: ji % 2 === 0 ? "#fff" : "#f8fafc" }}>
                        <td
                          onClick={() => aplicarFiltroPivot(jefe, "")}
                          style={{ ...tdStyle, fontWeight: "600", color: pivotJefe === jefe && !pivotTipoW ? C.red : C.navy, cursor: "pointer", background: pivotJefe === jefe && !pivotTipoW ? "#fef2f2" : "inherit" }}>
                          {jefe}
                        </td>
                        {pivotTipos.map((tipo, ti) => {
                          const val = pivotData[jefe]?.[tipo] || 0;
                          const activo = pivotJefe === jefe && pivotTipoW === tipo;
                          return (
                            <td key={ti}
                              onClick={() => val > 0 ? aplicarFiltroPivot(jefe, tipo) : null}
                              style={{ ...tdStyle, textAlign: "center", cursor: val > 0 ? "pointer" : "default",
                                background: activo ? "#dbeafe" : val > 0 ? "#f0fdf4" : "#f8fafc",
                                color: activo ? "#1d4ed8" : val > 0 ? "#15803d" : "#cbd5e1",
                                fontWeight: val > 0 ? "700" : "400",
                                border: activo ? "2px solid #3b82f6" : "1px solid #f1f5f9" }}>
                              {val > 0 ? val : "—"}
                            </td>
                          );
                        })}
                        <td style={{ ...tdStyle, textAlign: "center", fontWeight: "700", color: C.navy, background: "#f1f5f9" }}>
                          {pivotData[jefe]?.__total__ || 0}
                        </td>
                      </tr>
                    ))}
                    {/* Fila de totales */}
                    <tr style={{ background: "#1a2b4a" }}>
                      <td style={{ ...tdStyle, fontWeight: "700", color: "white", background: "#1a2b4a" }}>TOTAL</td>
                      {pivotTipos.map((tipo, ti) => (
                        <td key={ti} style={{ ...tdStyle, textAlign: "center", fontWeight: "700", color: "white", background: "#1a2b4a" }}>
                          {pivotTotalesTipo[tipo] || 0}
                        </td>
                      ))}
                      <td style={{ ...tdStyle, textAlign: "center", fontWeight: "700", color: "white", background: "#111827" }}>
                        {planesParaPivot.length}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ══ TABLA PIVOT ESTADOS POR MES ══ */}
          {pivotMesEstado.data.length > 0 && (
            <div style={cardStyle}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
                <span style={{ fontSize: "13px", fontWeight: "700", color: C.navy, textTransform: "uppercase", letterSpacing: "0.05em" }}>Estados de Planes por Mes de Ofensores</span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, minWidth: "110px" }}>Mes Ofensores</th>
                      {pivotMesEstado.estados.map((est, i) => {
                        const colEst = est === "Completado" ? "#15803d" : est === "En proceso" ? "#1d4ed8" : est === "Cancelado" ? "#dc2626" : "#92400e";
                        return <th key={i} style={{ ...thStyle, textAlign: "center", background: colEst, minWidth: "90px" }}>{est}</th>;
                      })}
                      <th style={{ ...thStyle, textAlign: "center", background: "#111827", minWidth: "70px" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pivotMesEstado.data.map((row, ri) => (
                      <tr key={ri} style={{ background: ri % 2 === 0 ? "#fff" : "#f8fafc" }}>
                        <td style={{ ...tdStyle, fontWeight: "700", color: C.navy }}>
                          {row.mes ? new Date(row.mes + "-01T12:00:00").toLocaleDateString("es-CO", { month: "short", year: "numeric" }).replace(".", "") : "—"}
                        </td>
                        {pivotMesEstado.estados.map((est, ei) => {
                          const val = row[est] || 0;
                          return (
                            <td key={ei} style={{ ...tdStyle, textAlign: "center",
                              background: val > 0 ? "#f0fdf4" : "#f8fafc",
                              color: val > 0 ? "#15803d" : "#cbd5e1",
                              fontWeight: val > 0 ? "700" : "400" }}>
                              {val > 0 ? val : "—"}
                            </td>
                          );
                        })}
                        <td style={{ ...tdStyle, textAlign: "center", fontWeight: "700", color: C.navy, background: "#f1f5f9" }}>{row.total}</td>
                      </tr>
                    ))}
                    <tr style={{ background: "#1a2b4a" }}>
                      <td style={{ ...tdStyle, fontWeight: "700", color: "white", background: "#1a2b4a" }}>TOTAL</td>
                      {pivotMesEstado.estados.map((est, ei) => (
                        <td key={ei} style={{ ...tdStyle, textAlign: "center", fontWeight: "700", color: "white", background: "#1a2b4a" }}>
                          {pivotMesEstado.totales[est] || 0}
                        </td>
                      ))}
                      <td style={{ ...tdStyle, textAlign: "center", fontWeight: "700", color: "white", background: "#111827" }}>{pivotMesEstado.totales.total}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* FILTROS */}
          <div style={cardStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
              <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
              <span style={{ fontSize: "13px", fontWeight: "700", color: C.navy, textTransform: "uppercase", letterSpacing: "0.05em" }}>Filtros</span>
              <span style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "20px", padding: "2px 10px", fontSize: "12px", color: "#475569", fontWeight: "600", marginLeft: "auto" }}>{planesFiltrados.length} planes</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "10px", alignItems: "end" }}>
              <div>
                <label style={labelStyle}>Buscar plan</label>
                <input type="text" placeholder="Nombre del plan..." value={filtroNombre} onChange={e => setFiltroNombre(e.target.value)} style={inputStyle} />
              </div>

              <div>
                <label style={labelStyle}>Buscar por nodo</label>
                <input type="text" placeholder="ID nodo (ej: CB7)..." value={busquedaNodoInverso} onChange={e => setBusquedaNodoInverso(e.target.value.toUpperCase())} style={inputStyle} />
              </div>

              {/* Multi-select Tipo de Trabajo */}
              <div style={{ position: "relative" }}>
                <label style={labelStyle}>Tipo de trabajo</label>
                <div onClick={() => { setDropdownTipo(v => !v); setDropdownEstado(false); }}
                  style={{ ...inputStyle, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", userSelect: "none" }}>
                  <span style={{ color: filtroTipo.length === 0 ? "#94a3b8" : C.navy, fontSize: "13px" }}>
                    {filtroTipo.length === 0 ? "Todos" : filtroTipo.length === 1 ? filtroTipo[0] : `${filtroTipo.length} seleccionados`}
                  </span>
                  <span style={{ fontSize: "10px", color: C.slate }}>▼</span>
                </div>
                {dropdownTipo && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1.5px solid #e2e8f0", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.12)", zIndex: 200, maxHeight: "220px", overflowY: "auto" }}>
                    <div onClick={() => setFiltroTipo([])} style={{ padding: "8px 12px", cursor: "pointer", fontSize: "12px", color: C.slate, borderBottom: "1px solid #f1f5f9", fontWeight: "600" }}>
                      ✕ Limpiar selección
                    </div>
                    {TIPOS_TRABAJO.filter(t => t !== "").map((t, i) => (
                      <div key={i} onClick={() => toggleFiltro(setFiltroTipo, t)}
                        style={{ padding: "8px 12px", cursor: "pointer", fontSize: "12px", display: "flex", alignItems: "center", gap: "8px", background: filtroTipo.includes(t) ? "#eff6ff" : "white", color: filtroTipo.includes(t) ? "#1d4ed8" : C.navy, fontWeight: filtroTipo.includes(t) ? "600" : "400" }}>
                        <span style={{ width: "14px", height: "14px", border: `2px solid ${filtroTipo.includes(t) ? "#1d4ed8" : "#cbd5e1"}`, borderRadius: "3px", background: filtroTipo.includes(t) ? "#1d4ed8" : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {filtroTipo.includes(t) && <span style={{ color: "white", fontSize: "9px", fontWeight: "800" }}>✓</span>}
                        </span>
                        {t}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Multi-select Estado */}
              <div style={{ position: "relative" }}>
                <label style={labelStyle}>Estado</label>
                <div onClick={() => { setDropdownEstado(v => !v); setDropdownTipo(false); }}
                  style={{ ...inputStyle, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", userSelect: "none" }}>
                  <span style={{ color: filtroEstado.length === 0 ? "#94a3b8" : C.navy, fontSize: "13px" }}>
                    {filtroEstado.length === 0 ? "Todos" : filtroEstado.length === 1 ? filtroEstado[0] : `${filtroEstado.length} seleccionados`}
                  </span>
                  <span style={{ fontSize: "10px", color: C.slate }}>▼</span>
                </div>
                {dropdownEstado && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1.5px solid #e2e8f0", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.12)", zIndex: 200, maxHeight: "220px", overflowY: "auto" }}>
                    <div onClick={() => setFiltroEstado([])} style={{ padding: "8px 12px", cursor: "pointer", fontSize: "12px", color: C.slate, borderBottom: "1px solid #f1f5f9", fontWeight: "600" }}>
                      ✕ Limpiar selección
                    </div>
                    {ESTADOS.map((e, i) => (
                      <div key={i} onClick={() => toggleFiltro(setFiltroEstado, e)}
                        style={{ padding: "8px 12px", cursor: "pointer", fontSize: "12px", display: "flex", alignItems: "center", gap: "8px", background: filtroEstado.includes(e) ? "#eff6ff" : "white", color: filtroEstado.includes(e) ? "#1d4ed8" : C.navy, fontWeight: filtroEstado.includes(e) ? "600" : "400" }}>
                        <span style={{ width: "14px", height: "14px", border: `2px solid ${filtroEstado.includes(e) ? "#1d4ed8" : "#cbd5e1"}`, borderRadius: "3px", background: filtroEstado.includes(e) ? "#1d4ed8" : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {filtroEstado.includes(e) && <span style={{ color: "white", fontSize: "9px", fontWeight: "800" }}>✓</span>}
                        </span>
                        {e}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Botón limpiar todos */}
              {(filtroTipo.length > 0 || filtroEstado.length > 0 || filtroNombre || busquedaNodoInverso) && (
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button onClick={() => { setFiltroTipo([]); setFiltroEstado([]); setFiltroNombre(""); setBusquedaNodoInverso(""); setDropdownTipo(false); setDropdownEstado(false); }}
                    style={{ ...inputStyle, background: "#f1f5f9", cursor: "pointer", color: C.slate, border: "1px solid #e2e8f0", fontWeight: "600" }}>
                    Limpiar filtros
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* TABLA PLANES */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Tipo Trabajo</th>
                  <th style={thStyle}>Responsable</th>
                  <th style={thStyle}>Aliado</th>
                  <th style={thStyle}>Ciudad</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Nodos</th>
                  <th style={thStyle}>OT</th>
                  <th style={thStyle}>Inicio</th>
                  <th style={thStyle}>Fin</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Estado OT</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Av. Esperado</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Av. Real</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {planesFiltrados.map((plan, i) => {
                  const ec  = estadoColor(plan.estado);
                  const col = semaforo(parseFloat(plan.avance_real), parseFloat(plan.avance_esperado));
                  return (
                    <tr key={plan.id} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                      <td style={{ ...tdStyle, fontWeight: "600", color: C.navy }}>{plan.tipo_trabajo}</td>
                      <td style={tdStyle}>{plan.responsable || "—"}</td>
                      <td style={{ ...tdStyle, fontSize: "11px", color: C.slate }}>{plan.aliado || "—"}</td>
                      <td style={{ ...tdStyle, fontSize: "11px", color: C.slate }}>{plan.ciudad || "—"}</td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <span
                          onClick={e => { e.stopPropagation(); abrirPopupNodos(plan); }}
                          title="Ver nodos de este plan"
                          style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: "12px", padding: "2px 10px", fontWeight: "700", cursor: "pointer", fontSize: "12px", border: "1px solid #93c5fd" }}>
                          {plan.total_nodos || 0}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontSize: "11px" }}>{plan.orden_trabajo || "—"}</td>
                      <td style={tdStyle}>{formatFecha(plan.fecha_inicio)}</td>
                      <td style={tdStyle}>{formatFecha(plan.fecha_fin)}</td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        {(() => { const eOT = estadoColor(plan.estado_ot || plan.estado); return <span style={{ background: eOT.bg, color: eOT.color, border: `1px solid ${eOT.border}`, borderRadius: "20px", padding: "2px 8px", fontSize: "11px", fontWeight: "700" }}>{plan.estado_ot || plan.estado}</span>; })()}
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <span style={{ background: "#e2e8f0", color: C.navy, borderRadius: "6px", padding: "3px 10px", fontWeight: "700", fontSize: "12px" }}>{parseFloat(plan.avance_esperado).toFixed(1)}%</span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <span style={{ background: col, color: "white", borderRadius: "6px", padding: "3px 10px", fontWeight: "700", fontSize: "12px" }}>{parseFloat(plan.avance_real).toFixed(1)}%</span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                          <button onClick={() => abrirDetalle(plan)} style={{ background: "#dbeafe", color: "#1d4ed8", border: "none", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>Ver</button>
                          {puedeEditar && (
                            <>
                              <button onClick={() => { setEditandoPlan(plan); setNodosForm({}); setFormPlan({ tipo_trabajo: plan.tipo_trabajo, descripcion: plan.descripcion || "", fecha_inicio: formatFecha(plan.fecha_inicio), fecha_fin: formatFecha(plan.fecha_fin), responsable: plan.responsable || "", aliado: plan.aliado || "", ciudad: plan.ciudad || "", estado_ot: plan.estado_ot || "Pendiente", orden_trabajo: plan.orden_trabajo || "", chg: plan.chg || "", estado: plan.estado, avance_real: plan.avance_real }); setShowModalPlan(true); }}
                                style={{ background: "#fef9c3", color: "#92400e", border: "none", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>Editar</button>
                              <button onClick={() => eliminarPlan(plan.id)} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>Eliminar</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {planesFiltrados.length === 0 && (
                  <tr><td colSpan={10} style={{ ...tdStyle, textAlign: "center", color: C.slate, padding: "30px" }}>No hay planes registrados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ══ VISTA DETALLE ══ */}
      {vistaActual === "detalle" && planSeleccionado && (
        <>
          <button onClick={() => setVista("lista")} style={{ ...btnGris, marginBottom: "16px" }}>← Volver a Planes</button>

          {/* Info del plan */}
          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
              <div>
                <h3 style={{ margin: "0 0 6px 0", color: C.navy, fontSize: "18px" }}>{planSeleccionado.nombre_plan}</h3>
                <span style={{ fontSize: "12px", color: C.slate }}>{planSeleccionado.tipo_trabajo}</span>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                {puedeEditar && (
                  <>
                    <button onClick={() => setShowModalNodos(true)} style={btnGris}>+ Agregar Nodos</button>
                    <button onClick={abrirModalAvance} style={btnRojo}>+ Registrar Avance</button>
                  </>
                )}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "12px", marginTop: "16px" }}>
              {[
                { label: "Inicio", value: formatFecha(planSeleccionado.fecha_inicio) },
                { label: "Fin", value: formatFecha(planSeleccionado.fecha_fin) },
                { label: "Responsable", value: planSeleccionado.responsable || "—" },
                { label: "Orden de Trabajo", value: planSeleccionado.orden_trabajo || "—" },
                { label: "CHG", value: planSeleccionado.chg || "—" },
                { label: "Estado", value: planSeleccionado.estado },
                { label: "Avance Real", value: `${parseFloat(planSeleccionado.avance_real).toFixed(1)}%`, color: semaforo(parseFloat(planSeleccionado.avance_real), parseFloat(planSeleccionado.avance_esperado)) },
                { label: "Avance Esperado", value: `${parseFloat(planSeleccionado.avance_esperado).toFixed(1)}%` },
                { label: "Nodos", value: nodosDelPlan.length },
                { label: "Avances registrados", value: avancesDelPlan.length },
              ].map((k, i) => (
                <div key={i} style={{ background: "#f8fafc", borderRadius: "8px", padding: "10px 14px" }}>
                  <div style={{ fontSize: "10px", color: C.slate, fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.06em" }}>{k.label}</div>
                  <div style={{ fontSize: "16px", fontWeight: "700", color: k.color || C.navy, marginTop: "2px" }}>{k.value}</div>
                </div>
              ))}
            </div>

            {planSeleccionado.descripcion && (
              <div style={{ marginTop: "14px", padding: "12px", background: "#f8fafc", borderRadius: "8px", fontSize: "13px", color: C.slate }}>
                {planSeleccionado.descripcion}
              </div>
            )}
          </div>

          {/* ══ PESTAÑAS ══ */}
          <div style={cardStyle}>
            {/* Tab bar */}
            <div style={{ display: "flex", gap: "4px", marginBottom: "20px", borderBottom: `2px solid #e2e8f0`, paddingBottom: "0" }}>
              {[
                { key: "nodos",       label: `Nodos (${nodosDelPlan.length})` },
                { key: "avances",     label: `Historial Avances (${avancesDelPlan.length})` },
                { key: "efectividad", label: "📊 Efectividad" },
              ].map(tab => (
                <button key={tab.key}
                  onClick={() => {
                    setTabDetalle(tab.key);
                    if (tab.key === "efectividad" && !efectividad && !loadingEfect) {
                      cargarEfectividad(planSeleccionado.id);
                    }
                  }}
                  style={{
                    background: tabDetalle === tab.key ? C.navy : "transparent",
                    color: tabDetalle === tab.key ? "white" : C.slate,
                    border: "none", borderRadius: "8px 8px 0 0",
                    padding: "8px 18px", fontSize: "13px", fontWeight: "600",
                    cursor: "pointer", marginBottom: "-2px",
                    borderBottom: tabDetalle === tab.key ? `2px solid ${C.navy}` : "2px solid transparent"
                  }}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* TAB: NODOS */}
            {tabDetalle === "nodos" && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Nodo</th>
                      <th style={thStyle}>Nombre</th>
                      <th style={thStyle}>Ciudad</th>
                      <th style={thStyle}>Jefe Zona</th>
                      <th style={thStyle}>Aliado</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Mes Ofensor</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Cat. Inicio</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Cat. Actual</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Cambio</th>
                      <th style={thStyle}>Causa Falla</th>
                      {puedeEditar && <th style={{ ...thStyle, textAlign: "center" }}>Quitar</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {nodosDelPlan.map((n, i) => {
                      // Categoria inicio: guardada en BD al agregar el nodo
                      const catInicio = n.categoria_inicio || n.categoria_actual || "—";
                      // Categoria actual: corte mas reciente (siempre en vivo)
                      const catActual = n.categoria_actual || "—";
                      const ordenCat = { "N0":0,"N1":1,"N2":2,"N3":3,"N4":4,"N5":5,"N6":6 };
                      const rankInicio = ordenCat[catInicio] ?? 99;
                      const rankActual = ordenCat[catActual] ?? 99;
                      // Cambio aplica solo si el plan esta activo (no completado)
                      const planActivo = planSeleccionado?.estado !== "Completado" && planSeleccionado?.estado !== "Cancelado";
                      const hayCambio = planActivo && catInicio !== "—" && catActual !== "—" && catInicio !== catActual;
                      const mejoro = hayCambio && rankActual < rankInicio;
                      const empeoro = hayCambio && rankActual > rankInicio;
                      const catColor = (cat) => cat === "N0" ? "#16a34a" : cat === "N1" ? "#22c55e" : cat === "N2" ? "#84cc16" : cat === "N3" ? "#eab308" : cat === "N4" ? "#f97316" : cat === "N5" ? "#ef4444" : cat === "N6" ? "#991b1b" : "#94a3b8";
                      return (
                        <tr key={i} style={{ background: mejoro ? "#f0fdf4" : empeoro ? "#fef2f2" : i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                          <td style={{ ...tdStyle, fontWeight: "700", color: C.navy }}>{n.nodo}</td>
                          <td style={tdStyle}>{n.NOMBRE_NODO || "—"}</td>
                          <td style={tdStyle}>{n.ciudad || "—"}</td>
                          <td style={tdStyle}>{n.nombre_jefe || "—"}</td>
                          <td style={tdStyle}>{n.aliado || "—"}</td>
                          <td style={{ ...tdStyle, textAlign: "center" }}>{formatMes(n.m_fecha_mes)}</td>
                          <td style={{ ...tdStyle, textAlign: "center" }}>
                            <span style={{ background: catColor(catInicio), color: "white", borderRadius: "12px", padding: "1px 8px", fontSize: "11px", fontWeight: "700" }}>{catInicio}</span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: "center" }}>
                            <span style={{ background: catColor(catActual), color: "white", borderRadius: "12px", padding: "1px 8px", fontSize: "11px", fontWeight: "700" }}>{catActual}</span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: "center" }}>
                            {!planActivo
                              ? <span style={{ color: C.light, fontSize: "11px" }}>—</span>
                              : mejoro
                                ? <span style={{ background: "#dcfce7", color: "#15803d", borderRadius: "20px", padding: "2px 8px", fontSize: "11px", fontWeight: "700" }}>↑ Mejoró</span>
                                : empeoro
                                  ? <span style={{ background: "#fef2f2", color: "#dc2626", borderRadius: "20px", padding: "2px 8px", fontSize: "11px", fontWeight: "700" }}>↓ Empeoró</span>
                                  : <span style={{ background: "#f1f5f9", color: C.slate, borderRadius: "20px", padding: "2px 8px", fontSize: "11px", fontWeight: "600" }}>= Sin cambio</span>
                            }
                          </td>
                          <td style={{ ...tdStyle, fontSize: "11px" }}>{n.causa_falla || "—"}</td>
                          {puedeEditar && (
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <button onClick={() => quitarNodo(n.nodo, formatFecha(n.fecha_corte))} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: "6px", padding: "3px 8px", fontSize: "11px", cursor: "pointer" }}>Quitar</button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {nodosDelPlan.length === 0 && (
                      <tr><td colSpan={puedeEditar ? 11 : 10} style={{ ...tdStyle, textAlign: "center", color: C.slate, padding: "20px" }}>No hay nodos agregados.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* TAB: AVANCES */}
            {tabDetalle === "avances" && (
              <>
                {avancesDelPlan.length > 0 && (
                  <div style={{ marginBottom: "20px" }}>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={[...avancesDelPlan].reverse().map(a => ({ ...a, fecha_label: a.fecha_registro ? new Date(a.fecha_registro).toLocaleDateString("es-CO", { day: "2-digit", month: "short" }) : "" }))} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="fecha_label" tick={{ fontSize: 11 }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => `${v}%`} labelFormatter={(label) => `Fecha: ${label}`} />
                        <Legend />
                        <Line type="monotone" dataKey="avance_real" name="Avance Real" stroke="#E30613" strokeWidth={2.5} dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="avance_esperado" name="Avance Esperado" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Fecha</th>
                        <th style={{ ...thStyle, textAlign: "center" }}>Avance Real</th>
                        <th style={{ ...thStyle, textAlign: "center" }}>Avance Esperado</th>
                        <th style={thStyle}>Observacion</th>
                        <th style={thStyle}>Registrado por</th>
                      </tr>
                    </thead>
                    <tbody>
                      {avancesDelPlan.map((a, i) => {
                        const col = semaforo(parseFloat(a.avance_real), parseFloat(a.avance_esperado));
                        return (
                          <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                            <td style={tdStyle}>{formatFechaHora(a.fecha_registro)}</td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <span style={{ background: col, color: "white", borderRadius: "6px", padding: "2px 8px", fontWeight: "700", fontSize: "11px" }}>{parseFloat(a.avance_real).toFixed(1)}%</span>
                            </td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <span style={{ background: "#e2e8f0", color: C.navy, borderRadius: "6px", padding: "2px 8px", fontWeight: "700", fontSize: "11px" }}>{parseFloat(a.avance_esperado).toFixed(1)}%</span>
                            </td>
                            <td style={tdStyle}>{a.observacion || "—"}</td>
                            <td style={tdStyle}>{a.registrado_por || "—"}</td>
                          </tr>
                        );
                      })}
                      {avancesDelPlan.length === 0 && (
                        <tr><td colSpan={5} style={{ ...tdStyle, textAlign: "center", color: C.slate, padding: "20px" }}>No hay avances registrados.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* TAB: EFECTIVIDAD */}
            {tabDetalle === "efectividad" && (
              <>
                {loadingEfect && (
                  <div style={{ textAlign: "center", padding: "40px", color: C.slate, fontSize: "14px" }}>Calculando efectividad...</div>
                )}
                {!loadingEfect && efectividad && efectividad.pendiente && (
                  <div style={{ background: "#fef9c3", border: "1px solid #fde68a", borderRadius: "10px", padding: "20px 24px", color: "#92400e", fontSize: "13px" }}>
                    <div style={{ fontWeight: "700", fontSize: "15px", marginBottom: "6px" }}>⏳ Plan aún en curso</div>
                    <div>{efectividad.mensaje}</div>
                  </div>
                )}
                {!loadingEfect && efectividad && !efectividad.pendiente && (() => {
                  const resumen = efectividad.resumen || { pctEfectividad: 0, efectivos: 0, parciales: 0, inefectivos: 0, sinDatos: 0, total: 0 };
                  const nodos   = efectividad.nodos   || [];
                  const plan    = efectividad.plan    || {};
                  return (
                  <>
                    {/* KPIs efectividad */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "12px", marginBottom: "20px" }}>
                      {[
                        { label: "Efectividad", value: `${resumen.pctEfectividad}%`, color: resumen.pctEfectividad >= 70 ? "#15803d" : resumen.pctEfectividad >= 40 ? "#ca8a04" : "#dc2626", bg: resumen.pctEfectividad >= 70 ? "#dcfce7" : resumen.pctEfectividad >= 40 ? "#fef9c3" : "#fef2f2" },
                        { label: "Efectivos",   value: resumen.efectivos,   color: "#15803d", bg: "#dcfce7" },
                        { label: "Parciales",   value: resumen.parciales,   color: "#ca8a04", bg: "#fef9c3" },
                        { label: "Inefectivos", value: resumen.inefectivos, color: "#dc2626", bg: "#fef2f2" },
                        { label: "Sin datos",   value: resumen.sinDatos,    color: C.slate,   bg: "#f1f5f9" },
                      ].map((k, i) => (
                        <div key={i} style={{ background: k.bg, borderRadius: "10px", padding: "12px 14px", border: `1px solid ${k.color}30` }}>
                          <div style={{ fontSize: "10px", color: k.color, fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.06em" }}>{k.label}</div>
                          <div style={{ fontSize: "22px", fontWeight: "800", color: k.color }}>{k.value}</div>
                        </div>
                      ))}
                    </div>

                    {/* Info de fechas de comparación */}
                    <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: "8px", padding: "10px 14px", marginBottom: "16px", fontSize: "12px", color: "#0369a1", display: "flex", gap: "20px", flexWrap: "wrap" }}>
                      <span>📅 <strong>Inicio plan:</strong> {formatFecha(plan.fecha_inicio)}</span>
                      <span>✅ <strong>Completado:</strong> {formatFecha(plan.fecha_completado)}</span>
                    </div>

                    {/* Filtros */}
                    <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
                      {["", "Efectivo", "Parcial", "Inefectivo"].map(f => (
                        <button key={f} onClick={() => setFiltroEfect(f)}
                          style={{ padding: "4px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "600", cursor: "pointer", border: "1px solid",
                            background: filtroEfect === f ? C.navy : "white",
                            color: filtroEfect === f ? "white" : C.slate,
                            borderColor: filtroEfect === f ? C.navy : "#e2e8f0" }}>
                          {f === "" ? "Todos" : f}
                        </button>
                      ))}
                    </div>

                    {/* Tabla nodo a nodo */}
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Nodo</th>
                            <th style={{ ...thStyle, textAlign: "center" }}>Cat. Antes</th>
                            <th style={{ ...thStyle, textAlign: "center" }}>Cat. Después</th>
                            <th style={{ ...thStyle, textAlign: "center" }}>Var. Disponib.</th>
                            <th style={{ ...thStyle, textAlign: "center" }}>Resultado</th>
                            <th style={{ ...thStyle, textAlign: "center" }}>Corte Antes</th>
                            <th style={{ ...thStyle, textAlign: "center" }}>Corte Después</th>
                          </tr>
                        </thead>
                        <tbody>
                          {nodos
                            .filter(n => filtroEfect === "" || n.resultado === filtroEfect)
                            .map((n, i) => {
                              const resColor = n.resultado === "Efectivo" ? "#15803d" : n.resultado === "Parcial" ? "#ca8a04" : n.resultado === "Inefectivo" ? "#dc2626" : C.slate;
                              const resBg    = n.resultado === "Efectivo" ? "#dcfce7" : n.resultado === "Parcial" ? "#fef9c3" : n.resultado === "Inefectivo" ? "#fef2f2" : "#f1f5f9";
                              const catColor = (cat) => cat === "N0" ? "#16a34a" : cat === "N1" ? "#22c55e" : cat === "N2" ? "#84cc16" : cat === "N3" ? "#eab308" : cat === "N4" ? "#f97316" : cat === "N5" ? "#ef4444" : "#991b1b";
                              return (
                                <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                                  <td style={{ ...tdStyle, fontWeight: "700", color: C.navy }}>{n.nodo}</td>
                                  <td style={{ ...tdStyle, textAlign: "center" }}>
                                    {n.catAntes ? <span style={{ background: catColor(n.catAntes), color: "white", borderRadius: "12px", padding: "1px 8px", fontSize: "11px", fontWeight: "700" }}>{n.catAntes}</span> : <span style={{ color: C.light }}>—</span>}
                                  </td>
                                  <td style={{ ...tdStyle, textAlign: "center" }}>
                                    {n.catDespues ? <span style={{ background: catColor(n.catDespues), color: "white", borderRadius: "12px", padding: "1px 8px", fontSize: "11px", fontWeight: "700" }}>{n.catDespues}</span> : <span style={{ color: C.light }}>—</span>}
                                  </td>
                                  <td style={{ ...tdStyle, textAlign: "center" }}>
                                    {n.varDisp != null
                                      ? <span style={{ color: n.varDisp > 0 ? "#15803d" : n.varDisp < 0 ? "#dc2626" : C.slate, fontWeight: "700" }}>{n.varDisp > 0 ? `+${n.varDisp}` : n.varDisp}%</span>
                                      : <span style={{ color: C.light }}>—</span>}
                                  </td>
                                  <td style={{ ...tdStyle, textAlign: "center" }}>
                                    <span style={{ background: resBg, color: resColor, borderRadius: "20px", padding: "2px 10px", fontSize: "11px", fontWeight: "700", border: `1px solid ${resColor}30` }}>{n.resultado}</span>
                                  </td>
                                  <td style={{ ...tdStyle, textAlign: "center", fontSize: "11px", color: C.slate }}>{formatFecha(n.corteAntes)}</td>
                                  <td style={{ ...tdStyle, textAlign: "center", fontSize: "11px", color: C.slate }}>{formatFecha(n.corteDespues)}</td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </>
                  );
                })()}
              </>
            )}
          </div>
        </>
      )}

      {/* ══ VISTA DASHBOARD ══ */}
      {vistaActual === "dashboard" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

          {/* ── FILA 1: KPIs gerenciales ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
            {[
              { label: "Total Planes",  val: planes.length, icon: "📋", color: "#0f172a", bg: "#f1f5f9", key: null },
              { label: "Pendientes",    val: planes.filter(p=>p.estado==="Pendiente").length, icon: "⏳", color: "#92400e", bg: "#fef9c3", key: "Pendiente" },
              { label: "En Proceso",    val: planes.filter(p=>p.estado==="En proceso").length, icon: "⚙️", color: "#1d4ed8", bg: "#dbeafe", key: "En proceso" },
              { label: "Completados",   val: planes.filter(p=>p.estado==="Completado").length, icon: "✅", color: "#15803d", bg: "#dcfce7", key: "Completado" },
              { label: "Cancelados",    val: planes.filter(p=>p.estado==="Cancelado").length, icon: "❌", color: "#dc2626", bg: "#fee2e2", key: "Cancelado" },
              { label: "Total Nodos",   val: planes.reduce((s,p)=>s+parseInt(p.total_nodos||0),0), icon: "📡", color: "#7c3aed", bg: "#ede9fe", key: null },
            ].map((k,i) => (
              <div key={i} onClick={() => k.key ? setDashFiltroEstado(prev => prev === k.key ? "" : k.key) : null}
                style={{ background: dashFiltroEstado === k.key && k.key ? k.color : "white",
                  color: dashFiltroEstado === k.key && k.key ? "white" : k.color,
                  border: `1.5px solid ${k.color}20`, borderRadius: "14px", padding: "14px 18px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)", cursor: k.key ? "pointer" : "default",
                  transition: "all 0.15s", display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "40px", height: "40px", borderRadius: "10px",
                  background: dashFiltroEstado === k.key && k.key ? "rgba(255,255,255,0.2)" : k.bg,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>{k.icon}</div>
                <div>
                  <div style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.07em", opacity: 0.7 }}>{k.label}</div>
                  <div style={{ fontSize: "26px", fontWeight: "800", lineHeight: 1.1 }}>{k.val}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── FILA 2: Gráfica gerencial + Tabla resumen macro ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>

            {/* Gráfica donut / avance global */}
            <div style={{ background: "white", borderRadius: "14px", border: "1px solid #e2e8f0", padding: "20px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
                <span style={{ fontSize: "13px", fontWeight: "700", color: C.navy }}>Resumen Ejecutivo</span>
              </div>
              {(() => {
                const total = planes.length || 1;
                const comp = planes.filter(p=>p.estado==="Completado").length;
                const enP  = planes.filter(p=>p.estado==="En proceso").length;
                const pend = planes.filter(p=>p.estado==="Pendiente").length;
                const canc = planes.filter(p=>p.estado==="Cancelado").length;
                const avgReal = planes.length > 0 ? Math.round(planes.reduce((s,p)=>s+parseFloat(p.avance_real||0),0)/planes.length) : 0;
                const avgEsp  = planes.length > 0 ? Math.round(planes.reduce((s,p)=>s+parseFloat(p.avance_esperado||0),0)/planes.length) : 0;
                const diff = avgReal - avgEsp;
                const semCol = diff >= 0 ? "#22c55e" : diff >= -10 ? "#f59e0b" : "#ef4444";
                const segs = [
                  { label: "Completado", val: comp, color: "#22c55e" },
                  { label: "En proceso", val: enP,  color: "#3b82f6" },
                  { label: "Pendiente",  val: pend, color: "#f59e0b" },
                  { label: "Cancelado",  val: canc, color: "#ef4444" },
                ].filter(s => s.val > 0);
                const circumference = 2 * Math.PI * 54;
                let offset = 0;
                const arcs = segs.map(s => {
                  const pct = s.val / total;
                  const arc = { ...s, pct, dash: pct * circumference, offset };
                  offset += pct * circumference;
                  return arc;
                });
                return (
                  <div>
                    {/* Donut SVG */}
                    <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "16px" }}>
                      <svg width="130" height="130" viewBox="0 0 120 120" style={{ flexShrink: 0 }}>
                        <circle cx="60" cy="60" r="54" fill="none" stroke="#f1f5f9" strokeWidth="14" />
                        {arcs.map((a, i) => (
                          <circle key={i} cx="60" cy="60" r="54" fill="none"
                            stroke={a.color} strokeWidth="14"
                            strokeDasharray={`${a.dash} ${circumference - a.dash}`}
                            strokeDashoffset={circumference / 4 - a.offset}
                            style={{ transform: "rotate(-90deg)", transformOrigin: "60px 60px" }} />
                        ))}
                        <text x="60" y="56" textAnchor="middle" style={{ fontSize: "18px", fontWeight: "800", fill: semCol }}>{avgReal}%</text>
                        <text x="60" y="70" textAnchor="middle" style={{ fontSize: "9px", fill: "#64748b" }}>avance real</text>
                      </svg>
                      <div style={{ flex: 1 }}>
                        {segs.map((s, i) => (
                          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <div style={{ width: "10px", height: "10px", borderRadius: "3px", background: s.color, flexShrink: 0 }} />
                              <span style={{ fontSize: "11px", color: C.slate }}>{s.label}</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <span style={{ fontSize: "12px", fontWeight: "700", color: C.navy }}>{s.val}</span>
                              <span style={{ fontSize: "10px", color: C.slate }}>({Math.round(s.val/total*100)}%)</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Avance real vs esperado global */}
                    <div style={{ background: "#f8fafc", borderRadius: "10px", padding: "12px 14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                        <span style={{ fontSize: "11px", color: C.slate, fontWeight: "600" }}>Avance promedio global</span>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                          <span style={{ fontSize: "14px", fontWeight: "800", color: semCol }}>{avgReal}%</span>
                          <span style={{ fontSize: "11px", color: C.slate }}>/ {avgEsp}% esp.</span>
                          <span style={{ fontSize: "11px", fontWeight: "700", color: semCol }}>{diff>=0?`+${diff}`:`${diff}`}%</span>
                        </div>
                      </div>
                      <div style={{ position: "relative", height: "10px", background: "#e2e8f0", borderRadius: "99px", overflow: "hidden" }}>
                        <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(avgEsp,100)}%`, background: "#cbd5e1", borderRadius: "99px" }} />
                        <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(avgReal,100)}%`, background: semCol, borderRadius: "99px", transition: "width 0.4s" }} />
                      </div>
                      <div style={{ display: "flex", gap: "12px", marginTop: "5px", fontSize: "10px", color: C.slate }}>
                        <span>⬜ Esperado</span><span style={{ color: semCol }}>■ Real</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Tabla resumen macro por jefe */}
            <div style={{ background: "white", borderRadius: "14px", border: "1px solid #e2e8f0", padding: "20px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
                <span style={{ fontSize: "13px", fontWeight: "700", color: C.navy }}>Cuadro de mando por Jefe</span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                  <thead>
                    <tr style={{ background: "#0f172a" }}>
                      {["Jefe","Planes","Nodos","Pend.","En proc.","Complet.","Canc.","Avance R.","Avance E.","Estado"].map((h,i) => (
                        <th key={i} style={{ padding: "7px 8px", color: "white", fontWeight: "600", textAlign: i===0?"left":"center", whiteSpace: "nowrap", fontSize: "10px" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.map((row, i) => {
                      const diff = row.avgAvanceReal - row.avgAvanceEsperado;
                      const semCol2 = diff >= 0 ? "#22c55e" : diff >= -10 ? "#f59e0b" : "#ef4444";
                      const badge = diff >= 0 ? { bg: "#dcfce7", color: "#15803d", label: "Al día" }
                        : diff >= -10 ? { bg: "#fef9c3", color: "#92400e", label: "En riesgo" }
                        : { bg: "#fee2e2", color: "#dc2626", label: "Atrasado" };
                      return (
                        <tr key={i}
                          onClick={() => setDashFiltroJefe(prev => prev === row.jefe ? "" : row.jefe)}
                          style={{ background: dashFiltroJefe === row.jefe ? "#fef2f2" : (i%2===0?"#fff":"#f8fafc"), cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "7px 8px", fontWeight: "600", color: dashFiltroJefe===row.jefe?"#E30613":C.navy, maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.jefe}</td>
                          <td style={{ padding: "7px 8px", textAlign: "center", fontWeight: "700", color: C.navy }}>{row.total}</td>
                          <td style={{ padding: "7px 8px", textAlign: "center", color: "#7c3aed", fontWeight: "600" }}>{row.totalNodos}</td>
                          <td style={{ padding: "7px 8px", textAlign: "center" }}>{row.pendientes > 0 ? <span style={{ background: "#fef9c3", color: "#92400e", borderRadius: "4px", padding: "1px 6px", fontWeight: "700" }}>{row.pendientes}</span> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                          <td style={{ padding: "7px 8px", textAlign: "center" }}>{row.enProceso > 0 ? <span style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: "4px", padding: "1px 6px", fontWeight: "700" }}>{row.enProceso}</span> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                          <td style={{ padding: "7px 8px", textAlign: "center" }}>{row.completados > 0 ? <span style={{ background: "#dcfce7", color: "#15803d", borderRadius: "4px", padding: "1px 6px", fontWeight: "700" }}>{row.completados}</span> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                          <td style={{ padding: "7px 8px", textAlign: "center" }}>{row.cancelados > 0 ? <span style={{ background: "#fee2e2", color: "#dc2626", borderRadius: "4px", padding: "1px 6px", fontWeight: "700" }}>{row.cancelados}</span> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                          <td style={{ padding: "7px 8px", textAlign: "center", fontWeight: "800", color: semCol2 }}>{row.avgAvanceReal}%</td>
                          <td style={{ padding: "7px 8px", textAlign: "center", color: C.slate }}>{row.avgAvanceEsperado}%</td>
                          <td style={{ padding: "7px 8px", textAlign: "center" }}><span style={{ background: badge.bg, color: badge.color, borderRadius: "20px", padding: "2px 7px", fontSize: "10px", fontWeight: "700", whiteSpace: "nowrap" }}>{badge.label}</span></td>
                        </tr>
                      );
                    })}
                    <tr style={{ background: "#0f172a" }}>
                      <td style={{ padding: "7px 8px", color: "white", fontWeight: "700", fontSize: "10px" }}>TOTAL</td>
                      <td style={{ padding: "7px 8px", textAlign: "center", color: "white", fontWeight: "700" }}>{planes.length}</td>
                      <td style={{ padding: "7px 8px", textAlign: "center", color: "white", fontWeight: "700" }}>{planes.reduce((s,p)=>s+parseInt(p.total_nodos||0),0)}</td>
                      <td style={{ padding: "7px 8px", textAlign: "center", color: "#fde68a", fontWeight: "700" }}>{planes.filter(p=>p.estado==="Pendiente").length}</td>
                      <td style={{ padding: "7px 8px", textAlign: "center", color: "#93c5fd", fontWeight: "700" }}>{planes.filter(p=>p.estado==="En proceso").length}</td>
                      <td style={{ padding: "7px 8px", textAlign: "center", color: "#86efac", fontWeight: "700" }}>{planes.filter(p=>p.estado==="Completado").length}</td>
                      <td style={{ padding: "7px 8px", textAlign: "center", color: "#fca5a5", fontWeight: "700" }}>{planes.filter(p=>p.estado==="Cancelado").length}</td>
                      <td colSpan={3} style={{ padding: "7px 8px", textAlign: "center", color: "rgba(255,255,255,0.6)", fontSize: "10px" }}>— avances por jefe arriba —</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── FILA 3: Tabla semáforo Jefe × Mes + Panel lateral ── */}
          <div style={{ background: "white", borderRadius: "14px", border: "1px solid #e2e8f0", padding: "20px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
              <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
              <span style={{ fontSize: "13px", fontWeight: "700", color: C.navy }}>Semáforo de Planes — Jefe × Mes</span>
              <span style={{ fontSize: "11px", color: C.slate, marginLeft: "4px" }}>— haz clic en una celda para ver y gestionar los planes</span>
            </div>
            <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>

              {/* Tabla semáforo */}
              <div style={{ overflowX: "auto", flex: 1 }}>
                <table style={{ borderCollapse: "collapse", fontSize: "11px", width: "100%" }}>
                  <thead>
                    <tr>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: "11px", color: C.slate, fontWeight: "600", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>Jefe</th>
                      {mesesPivot.map((m, i) => (
                        <th key={i} style={{ padding: "8px 8px", textAlign: "center", fontSize: "11px", color: C.slate, fontWeight: "600", borderBottom: "1px solid #e2e8f0", minWidth: "120px", whiteSpace: "nowrap" }}>
                          {new Date(m + "-01T12:00:00").toLocaleDateString("es-CO", { month: "short", year: "numeric" }).replace(".", "")}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.map((row, ji) => (
                      <tr key={ji} style={{ background: ji%2===0?"#fff":"#f8fafc" }}>
                        <td style={{ padding: "10px 12px", fontSize: "12px", fontWeight: "600", color: C.navy, whiteSpace: "nowrap", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis" }}>{row.jefe}</td>
                        {mesesPivot.map((m, mi) => {
                          const ps = row.planesDetalle.filter(p => p.mes_ofensores === m);
                          if (!ps.length) return <td key={mi} style={{ padding: "6px 4px", textAlign: "center", color: "#cbd5e1", fontSize: "11px" }}>—</td>;
                          const counts = { Pendiente: ps.filter(p=>p.estado==="Pendiente").length, "En proceso": ps.filter(p=>p.estado==="En proceso").length, Completado: ps.filter(p=>p.estado==="Completado").length, Cancelado: ps.filter(p=>p.estado==="Cancelado").length };
                          const avgAv = Math.round(ps.reduce((s,p)=>s+parseFloat(p.avance_real||0),0)/ps.length);
                          const sc = avgAv >= 70 ? "#22c55e" : avgAv >= 40 ? "#f59e0b" : "#ef4444";
                          const isSelected = semaforoSeleccion && semaforoSeleccion.jefe === row.jefe && semaforoSeleccion.mes === m;
                          return (
                            <td key={mi} style={{ padding: "4px 3px" }}>
                              <button onClick={() => setSemaforoSeleccion(prev => prev?.jefe===row.jefe&&prev?.mes===m ? null : { jefe: row.jefe, mes: m, planes: ps })}
                                style={{ width: "100%", border: isSelected ? "1.5px solid #0f172a" : "0.5px solid #e2e8f0", borderRadius: "8px", padding: "7px 8px", cursor: "pointer", background: isSelected?"#f1f5f9":"white", textAlign: "left", transition: "all 0.12s" }}>
                                <div style={{ fontSize: "10px", fontWeight: "600", color: sc, marginBottom: "5px", display: "flex", justifyContent: "space-between" }}>
                                  <span>● {avgAv}%</span><span style={{ color: C.slate, fontWeight: "400" }}>{ps.length}p</span>
                                </div>
                                <div style={{ display: "flex", gap: "2px", flexWrap: "wrap" }}>
                                  {[["Pendiente","#f59e0b"],["En proceso","#3b82f6"],["Completado","#22c55e"],["Cancelado","#ef4444"]].map(([e,c]) =>
                                    counts[e] > 0 ? <span key={e} style={{ background: c+"20", color: c, borderRadius: "4px", padding: "1px 5px", fontSize: "10px", fontWeight: "600" }}>{counts[e]}</span> : null
                                  )}
                                </div>
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Leyenda */}
                <div style={{ display: "flex", gap: "16px", marginTop: "10px", fontSize: "10px", color: C.slate, flexWrap: "wrap" }}>
                  <span>● <span style={{ color: "#22c55e", fontWeight: "700" }}>≥70%</span> Al día</span>
                  <span>● <span style={{ color: "#f59e0b", fontWeight: "700" }}>40–69%</span> En riesgo</span>
                  <span>● <span style={{ color: "#ef4444", fontWeight: "700" }}>&lt;40%</span> Atrasado</span>
                  {[["#f59e0b","Pend."],["#3b82f6","En proc."],["#22c55e","Comp."],["#ef4444","Canc."]].map(([c,l])=>(
                    <span key={l} style={{ display: "flex", alignItems: "center", gap: "3px" }}><span style={{ width: "8px", height: "8px", borderRadius: "2px", background: c+"20", border: `1px solid ${c}`, display: "inline-block" }} />{l}</span>
                  ))}
                </div>
              </div>

              {/* Panel lateral de planes */}
              {semaforoSeleccion && (
                <div style={{ width: "290px", flexShrink: 0, border: "0.5px solid #e2e8f0", borderRadius: "12px", overflow: "hidden", background: "white" }}>
                  {/* Header */}
                  <div style={{ padding: "12px 14px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontSize: "10px", color: C.slate, marginBottom: "2px" }}>
                          {new Date(semaforoSeleccion.mes + "-01T12:00:00").toLocaleDateString("es-CO",{month:"long",year:"numeric"}).replace(".","").replace(/^\w/,c=>c.toUpperCase())}
                        </div>
                        <div style={{ fontSize: "12px", fontWeight: "700", color: C.navy, maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{semaforoSeleccion.jefe}</div>
                      </div>
                      <button onClick={() => setSemaforoSeleccion(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "16px", color: C.slate, padding: "0", lineHeight: 1 }}>×</button>
                    </div>
                    {/* Filtros de estado */}
                    <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", marginTop: "8px" }}>
                      {(() => {
                        const ps = semaforoSeleccion.planes;
                        const counts = { all: ps.length };
                        ["Pendiente","En proceso","Completado","Cancelado"].forEach(e => { counts[e] = ps.filter(p=>p.estado===e).length; });
                        const colors = { "Pendiente":"#f59e0b","En proceso":"#3b82f6","Completado":"#22c55e","Cancelado":"#ef4444" };
                        return [["all","Todos",null],...["Pendiente","En proceso","Completado","Cancelado"].filter(e=>counts[e]>0).map(e=>[e,e,colors[e]])].map(([key,label,col])=>(
                          <button key={key}
                            onClick={() => setSemaforoFiltroEstado(prev => prev === key ? "all" : key)}
                            style={{ padding: "2px 8px", borderRadius: "20px", border: `0.5px solid ${col||"#94a3b8"}`, fontSize: "10px", cursor: "pointer",
                              background: (semaforoFiltroEstado||"all") === key ? (col||"#0f172a") : "transparent",
                              color: (semaforoFiltroEstado||"all") === key ? "white" : (col||C.slate), fontWeight: "600" }}>
                            {label} ({counts[key]})
                          </button>
                        ));
                      })()}
                    </div>
                  </div>
                  {/* Lista de planes */}
                  <div style={{ maxHeight: "380px", overflowY: "auto", padding: "10px" }}>
                    {semaforoSeleccion.planes
                      .filter(p => !semaforoFiltroEstado || semaforoFiltroEstado === "all" || p.estado === semaforoFiltroEstado)
                      .map((p, pi) => {
                        const ec = estadoColor(p.estado);
                        const avR = parseFloat(p.avance_real||0);
                        const avE = parseFloat(p.avance_esperado||0);
                        const diff2 = avR - avE;
                        const bc2 = diff2>=0?"#22c55e":diff2>=-15?"#f59e0b":"#ef4444";
                        return (
                          <div key={pi}
                            onClick={() => { setPlan(p); setShowModalAvance(true); setFormAvance({ avance_real: p.avance_real||0, observacion: "" }); }}
                            style={{ border: "0.5px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", marginBottom: "8px",
                              cursor: "pointer", transition: "all 0.12s", borderLeft: `3px solid ${bc2}` }}
                            onMouseEnter={e=>e.currentTarget.style.background="#f8fafc"}
                            onMouseLeave={e=>e.currentTarget.style.background="white"}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "6px", marginBottom: "5px" }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: "12px", fontWeight: "600", color: C.navy, marginBottom: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.nombre_plan}</div>
                                <div style={{ fontSize: "10px", color: C.slate }}>{p.tipo_trabajo}</div>
                              </div>
                              <span style={{ background: ec.bg, color: ec.color, border: `1px solid ${ec.border}`, borderRadius: "20px", padding: "1px 7px", fontSize: "9px", fontWeight: "700", flexShrink: 0 }}>{p.estado}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", color: C.slate, marginBottom: "3px" }}>
                              <span>Avance</span>
                              <span style={{ color: bc2, fontWeight: "600" }}>{avR.toFixed(0)}% <span style={{ color: C.slate, fontWeight: "400" }}>/ {avE.toFixed(0)}% esp.</span> <span style={{ color: bc2 }}>{diff2>=0?`+${diff2.toFixed(0)}`:`${diff2.toFixed(0)}`}%</span></span>
                            </div>
                            <div style={{ position: "relative", height: "5px", background: "#e2e8f0", borderRadius: "99px", overflow: "hidden" }}>
                              <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(avE,100)}%`, background: "#cbd5e1" }} />
                              <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(avR,100)}%`, background: bc2, transition: "width 0.3s" }} />
                            </div>
                            <div style={{ marginTop: "5px", fontSize: "10px", color: "#3b82f6", textAlign: "right" }}>✏️ Registrar avance →</div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── FILA 4: Tarjetas expandibles por jefe (filtradas) ── */}
          {(dashFiltroJefe || dashFiltroEstado) && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
              <span style={{ fontSize: "13px", color: "#E30613", fontWeight: "600" }}>🔍 Filtro activo: {[dashFiltroJefe, dashFiltroEstado].filter(Boolean).join(" · ")}</span>
              <button onClick={()=>{setDashFiltroJefe("");setDashFiltroEstado("");}}
                style={{ background: "#E30613", color: "white", border: "none", borderRadius: "6px", padding: "4px 12px", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>✕ Limpiar</button>
            </div>
          )}

          {(dashFiltroJefe || dashFiltroEstado) && (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {dashboardDataFiltrado.map((row, i) => {
                const col = semaforo(row.avgAvanceReal, row.avgAvanceEsperado);
                const expandido = jefeExpandido === row.jefe;
                const diff = row.avgAvanceReal - row.avgAvanceEsperado;
                const badge = diff>=0?{bg:"#dcfce7",color:"#15803d",label:"Al día"}:diff>=-10?{bg:"#fef9c3",color:"#92400e",label:"En riesgo"}:{bg:"#fee2e2",color:"#dc2626",label:"Atrasado"};
                return (
                  <div key={i} style={{ background: "white", borderRadius: "14px", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" }}>
                    <div onClick={()=>setJefeExpandido(expandido?null:row.jefe)}
                      style={{ padding: "14px 18px", cursor: "pointer", borderLeft: `4px solid ${col}`, display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: "160px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
                          <span style={{ fontSize: "10px", color: C.slate }}>{expandido?"▼":"▶"}</span>
                          <span style={{ fontSize: "13px", fontWeight: "700", color: C.navy }}>{row.jefe}</span>
                          <span style={{ background: badge.bg, color: badge.color, borderRadius: "20px", padding: "1px 8px", fontSize: "10px", fontWeight: "700" }}>{badge.label}</span>
                        </div>
                        <div style={{ display: "flex", gap: "10px", fontSize: "11px", color: C.slate, flexWrap: "wrap" }}>
                          <span>📋 {row.total} planes</span><span>📡 {row.totalNodos} nodos</span>
                          <span style={{ color: "#3b82f6" }}>⚙ {row.enProceso}</span><span style={{ color: "#22c55e" }}>✓ {row.completados}</span>
                          {row.cancelados>0&&<span style={{ color: "#ef4444" }}>✕ {row.cancelados}</span>}
                        </div>
                      </div>
                      <div style={{ flex: 2, minWidth: "200px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                          <span style={{ fontSize: "11px", color: C.slate }}>Avance Real vs Esperado</span>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <span style={{ fontSize: "13px", fontWeight: "800", color: col }}>{row.avgAvanceReal}%</span>
                            <span style={{ fontSize: "11px", color: C.slate }}>/ {row.avgAvanceEsperado}%</span>
                          </div>
                        </div>
                        <div style={{ position: "relative", height: "8px", background: "#e2e8f0", borderRadius: "99px", overflow: "hidden" }}>
                          <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(row.avgAvanceEsperado,100)}%`, background: "#cbd5e1", borderRadius: "99px" }} />
                          <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(row.avgAvanceReal,100)}%`, background: col, borderRadius: "99px" }} />
                        </div>
                      </div>
                    </div>
                    {expandido && (
                      <div style={{ padding: "0 18px 14px 18px", borderTop: "1px solid #f1f5f9" }}>
                        <div style={{ fontSize: "11px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.05em", padding: "10px 0 8px" }}>Planes</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          {row.planesDetalle.map((p, pi) => {
                            const ec = estadoColor(p.estado);
                            const col2 = semaforo(parseFloat(p.avance_real), parseFloat(p.avance_esperado));
                            return (
                              <div key={pi} style={{ background: "#f8fafc", borderRadius: "8px", padding: "10px 12px", border: "1px solid #e2e8f0", borderLeft: `3px solid ${col2}` }}>
                                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "6px", marginBottom: "6px" }}>
                                  <div>
                                    <div style={{ fontSize: "12px", fontWeight: "700", color: C.navy, marginBottom: "2px" }}>{p.nombre_plan}</div>
                                    <div style={{ fontSize: "10px", color: C.slate }}>{p.tipo_trabajo}</div>
                                  </div>
                                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <span style={{ background: ec.bg, color: ec.color, border: `1px solid ${ec.border}`, borderRadius: "20px", padding: "2px 8px", fontSize: "10px", fontWeight: "700" }}>{p.estado}</span>
                                    {puedeEditar && <>
                                      <button onClick={()=>{setEditandoPlan(p);setNodosForm({});setFormPlan({tipo_trabajo:p.tipo_trabajo,descripcion:p.descripcion||"",fecha_inicio:formatFecha(p.fecha_inicio),fecha_fin:formatFecha(p.fecha_fin),responsable:p.responsable||"",aliado:p.aliado||"",ciudad:p.ciudad||"",estado_ot:p.estado_ot||"Pendiente",orden_trabajo:p.orden_trabajo||"",chg:p.chg||"",estado:p.estado,avance_real:p.avance_real});setShowModalPlan(true);}}
                                        style={{ background:"#fef9c3",color:"#92400e",border:"none",borderRadius:"6px",padding:"2px 8px",fontSize:"10px",fontWeight:"600",cursor:"pointer" }}>Editar</button>
                                      <button onClick={()=>{setPlan(p);setShowModalAvance(true);setFormAvance({avance_real:p.avance_real||0,observacion:""});}}
                                        style={{ background:"#dcfce7",color:"#166534",border:"none",borderRadius:"6px",padding:"2px 8px",fontSize:"10px",fontWeight:"600",cursor:"pointer" }}>Avance</button>
                                    </>}
                                  </div>
                                </div>
                                <div style={{ position: "relative", height: "6px", background: "#e2e8f0", borderRadius: "99px", overflow: "hidden" }}>
                                  <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(parseFloat(p.avance_esperado),100)}%`, background: "#cbd5e1" }} />
                                  <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(parseFloat(p.avance_real),100)}%`, background: col2, transition: "width 0.3s" }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

        </div>
      )}

      {/* ══ VISTA SEGUIMIENTO ══ */}
      {vistaActual === "seguimiento" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

          {/* Sub-navegación */}
          <div style={{ display: "flex", gap: "0", borderBottom: "2px solid #e2e8f0" }}>
            {[
              { key: "kanban",   label: "Kanban" },
              { key: "gantt",    label: "Gantt" },
              { key: "graficas", label: "Gráficas" },
            ].map(t => (
              <button key={t.key} onClick={() => setSubVistaSeg(t.key)}
                style={{ padding: "10px 22px", border: "none", cursor: "pointer", fontSize: "13px",
                  fontWeight: subVistaSeg === t.key ? "700" : "400",
                  background: "transparent",
                  color: subVistaSeg === t.key ? C.red : C.slate,
                  borderBottom: subVistaSeg === t.key ? `2px solid ${C.red}` : "2px solid transparent",
                  marginBottom: "-2px" }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ═══ KANBAN ═══ */}
          {subVistaSeg === "kanban" && (() => {
            const cols = [
              { key: "Pendiente",  label: "Pendiente",  bg: "#fef9c3", color: "#92400e", border: "#fde68a" },
              { key: "En proceso", label: "En proceso", bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" },
              { key: "Completado", label: "Completado", bg: "#dcfce7", color: "#15803d", border: "#86efac" },
              { key: "Cancelado",  label: "Cancelado",  bg: "#fee2e2", color: "#dc2626", border: "#fca5a5" },
            ];
            return (
              <div style={{ overflowX: "auto" }}>
                <div style={{ display: "flex", gap: "12px", minWidth: "700px" }}>
                  {cols.map(col => {
                    const ps = planes.filter(p => p.estado === col.key);
                    return (
                      <div key={col.key} style={{ flex: 1, minWidth: "200px", background: "#f8fafc", borderRadius: "12px", padding: "12px" }}>
                        {/* Cabecera columna */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                          <span style={{ fontSize: "12px", fontWeight: "700", color: col.color, textTransform: "uppercase", letterSpacing: "0.05em" }}>{col.label}</span>
                          <span style={{ background: col.bg, color: col.color, border: `1px solid ${col.border}`, borderRadius: "20px", padding: "1px 9px", fontSize: "11px", fontWeight: "700" }}>{ps.length}</span>
                        </div>
                        {/* Tarjetas */}
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {ps.map((p, i) => {
                            const avR = parseFloat(p.avance_real || 0);
                            const avE = parseFloat(p.avance_esperado || 0);
                            const diff = avR - avE;
                            const bc = diff >= 0 ? "#15803d" : diff >= -10 ? "#d97706" : "#dc2626";
                            const diasRestantes = p.fecha_fin ? Math.ceil((new Date(p.fecha_fin) - new Date()) / (1000*60*60*24)) : null;
                            return (
                              <div key={i}
                                onClick={() => abrirDetalle(p)}
                                style={{ background: "white", borderRadius: "10px", padding: "12px", border: "1px solid #e2e8f0", borderLeft: `3px solid ${bc}`, cursor: "pointer", transition: "all 0.12s" }}
                                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"}
                                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                                {/* Nombre */}
                                <div style={{ fontSize: "12px", fontWeight: "700", color: C.navy, marginBottom: "4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.nombre_plan}</div>
                                {/* Tipo y responsable */}
                                <div style={{ fontSize: "10px", color: C.slate, marginBottom: "8px" }}>{p.tipo_trabajo}</div>
                                <div style={{ fontSize: "10px", color: C.slate, marginBottom: "8px", display: "flex", alignItems: "center", gap: "4px" }}>
                                  <span style={{ background: "#f1f5f9", borderRadius: "4px", padding: "1px 6px", fontWeight: "600" }}>{p.responsable || "Sin responsable"}</span>
                                </div>
                                {/* Barra avance */}
                                <div style={{ position: "relative", height: "5px", background: "#e2e8f0", borderRadius: "99px", overflow: "hidden", marginBottom: "4px" }}>
                                  <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(avE,100)}%`, background: "#cbd5e1" }} />
                                  <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(avR,100)}%`, background: bc, transition: "width 0.3s" }} />
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px" }}>
                                  <span style={{ color: bc, fontWeight: "700" }}>{avR.toFixed(0)}%</span>
                                  {diasRestantes !== null && (
                                    <span style={{ color: diasRestantes < 0 ? "#dc2626" : diasRestantes <= 3 ? "#d97706" : C.slate, fontWeight: diasRestantes <= 3 ? "700" : "400" }}>
                                      {diasRestantes < 0 ? `Venció hace ${Math.abs(diasRestantes)}d` : diasRestantes === 0 ? "Vence hoy" : `${diasRestantes}d restantes`}
                                    </span>
                                  )}
                                </div>
                                {/* Nodos y OT */}
                                <div style={{ display: "flex", gap: "6px", marginTop: "6px", flexWrap: "wrap" }}>
                                  {p.total_nodos > 0 && <span style={{ background: "#ede9fe", color: "#6d28d9", borderRadius: "4px", padding: "1px 6px", fontSize: "10px", fontWeight: "600" }}>📡 {p.total_nodos} nodos</span>}
                                  {p.orden_trabajo && <span style={{ background: "#f1f5f9", color: C.slate, borderRadius: "4px", padding: "1px 6px", fontSize: "10px" }}>{p.orden_trabajo}</span>}
                                </div>
                              </div>
                            );
                          })}
                          {ps.length === 0 && (
                            <div style={{ textAlign: "center", padding: "20px 0", color: "#cbd5e1", fontSize: "12px" }}>Sin planes</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ═══ GANTT ═══ */}
          {subVistaSeg === "gantt" && (() => {
            const hoy = new Date();
            hoy.setHours(0,0,0,0);
            // Calcular rango global de fechas
            const fechasValidas = planes.filter(p => p.fecha_inicio && p.fecha_fin);
            if (fechasValidas.length === 0) return <div style={{ textAlign: "center", padding: "40px", color: C.slate }}>No hay planes con fechas de inicio y fin para mostrar el Gantt.</div>;

            const minDate = fechasValidas.reduce((m, p) => { const d = new Date(p.fecha_inicio); return d < m ? d : m; }, new Date(fechasValidas[0].fecha_inicio));
            const maxDate = fechasValidas.reduce((m, p) => { const d = new Date(p.fecha_fin); return d > m ? d : m; }, new Date(fechasValidas[0].fecha_fin));
            // Extender rango un poco
            minDate.setDate(minDate.getDate() - 2);
            maxDate.setDate(maxDate.getDate() + 2);
            const totalDias = Math.ceil((maxDate - minDate) / (1000*60*60*24));
            const pctPos = (fecha) => Math.max(0, Math.min(100, ((new Date(fecha) - minDate) / (maxDate - minDate)) * 100));
            const fmtD = (f) => f ? new Date(f).toLocaleDateString("es-CO", { day: "2-digit", month: "short" }) : "";

            // Generar marcas de semanas
            const semanas = [];
            let d = new Date(minDate);
            while (d <= maxDate) {
              semanas.push(new Date(d));
              d.setDate(d.getDate() + 7);
            }

            const EC = { Pendiente:"#f59e0b", "En proceso":"#3b82f6", Completado:"#22c55e", Cancelado:"#9ca3af" };

            return (
              <div style={cardStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                  <div style={{ width: "3px", height: "16px", background: C.red, borderRadius: "2px" }} />
                  <span style={{ fontSize: "13px", fontWeight: "700", color: C.navy }}>Diagrama de Gantt</span>
                  <span style={{ fontSize: "11px", color: C.slate }}>{fmtD(minDate)} → {fmtD(maxDate)}</span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <div style={{ minWidth: "600px" }}>
                    {/* Header fechas */}
                    <div style={{ display: "flex", marginBottom: "6px", paddingLeft: "200px" }}>
                      <div style={{ flex: 1, position: "relative", height: "20px" }}>
                        {semanas.map((s, i) => (
                          <div key={i} style={{ position: "absolute", left: `${pctPos(s)}%`, fontSize: "10px", color: C.slate, transform: "translateX(-50%)", whiteSpace: "nowrap" }}>
                            {s.toLocaleDateString("es-CO", { day: "2-digit", month: "short" })}
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Línea de hoy */}
                    {hoy >= minDate && hoy <= maxDate && (
                      <div style={{ position: "relative", display: "flex", marginBottom: "4px", paddingLeft: "200px" }}>
                        <div style={{ flex: 1, position: "relative" }}>
                          <div style={{ position: "absolute", left: `${pctPos(hoy)}%`, top: 0, bottom: 0, width: "2px", background: C.red, zIndex: 2, height: "100%" }} />
                        </div>
                      </div>
                    )}
                    {/* Filas de planes */}
                    {planes.filter(p => p.fecha_inicio && p.fecha_fin).map((p, i) => {
                      const avR = parseFloat(p.avance_real || 0);
                      const color = EC[p.estado] || "#94a3b8";
                      const left = pctPos(p.fecha_inicio);
                      const width = Math.max(0.5, pctPos(p.fecha_fin) - left);
                      const diff = parseFloat(p.avance_real||0) - parseFloat(p.avance_esperado||0);
                      const bc = diff >= 0 ? "#15803d" : diff >= -10 ? "#d97706" : "#dc2626";
                      return (
                        <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: "6px", minHeight: "36px" }}>
                          {/* Label plan */}
                          <div style={{ width: "200px", paddingRight: "10px", flexShrink: 0 }}>
                            <div style={{ fontSize: "11px", fontWeight: "600", color: C.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.nombre_plan}>{p.nombre_plan}</div>
                            <div style={{ fontSize: "10px", color: C.slate }}>{p.responsable?.split(" ")[0] || "—"}</div>
                          </div>
                          {/* Barra gantt */}
                          <div style={{ flex: 1, position: "relative", height: "28px", background: "#f1f5f9", borderRadius: "4px", overflow: "visible" }}>
                            {/* Línea hoy */}
                            {hoy >= minDate && hoy <= maxDate && (
                              <div style={{ position: "absolute", left: `${pctPos(hoy)}%`, top: 0, bottom: 0, width: "1.5px", background: C.red, zIndex: 3 }} />
                            )}
                            {/* Barra base (esperado) */}
                            <div style={{ position: "absolute", left: `${left}%`, width: `${width}%`, height: "100%", background: color + "33", borderRadius: "4px" }} />
                            {/* Barra real (avance) */}
                            <div style={{ position: "absolute", left: `${left}%`, width: `${width * avR / 100}%`, height: "100%", background: color, borderRadius: "4px", transition: "width 0.4s" }} />
                            {/* Etiqueta */}
                            <div style={{ position: "absolute", left: `${left}%`, top: "50%", transform: "translateY(-50%)", paddingLeft: "6px", fontSize: "10px", color: "white", fontWeight: "700", whiteSpace: "nowrap", zIndex: 2, textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>
                              {width > 8 ? `${avR.toFixed(0)}%` : ""}
                            </div>
                          </div>
                          {/* Badge estado y días */}
                          <div style={{ width: "80px", paddingLeft: "8px", flexShrink: 0, fontSize: "10px" }}>
                            <span style={{ fontWeight: "700", color: bc }}>{avR.toFixed(0)}%</span>
                            <div style={{ color: C.slate }}>{fmtD(p.fecha_inicio)}→{fmtD(p.fecha_fin)}</div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Leyenda */}
                    <div style={{ display: "flex", gap: "14px", marginTop: "12px", paddingTop: "10px", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", paddingLeft: "200px" }}>
                      {Object.entries(EC).map(([k,c]) => (
                        <span key={k} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: C.slate }}>
                          <span style={{ width: "10px", height: "10px", background: c, borderRadius: "2px", display: "inline-block" }} />{k}
                        </span>
                      ))}
                      <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: C.red, marginLeft: "auto" }}>
                        <span style={{ width: "2px", height: "12px", background: C.red, display: "inline-block" }} /> Hoy
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ═══ GRÁFICAS ═══ */}
          {subVistaSeg === "graficas" && (() => {
            // Datos para gráfica de avance por plan (últimos avances)
            const dataAvance = planes
              .filter(p => p.estado !== "Cancelado")
              .map(p => ({
                nombre: p.nombre_plan.length > 18 ? p.nombre_plan.slice(0,18)+"…" : p.nombre_plan,
                real: parseFloat(p.avance_real || 0),
                esperado: parseFloat(p.avance_esperado || 0),
              }));

            // Avance por tipo de trabajo
            const porTipo = {};
            planes.forEach(p => {
              if (!porTipo[p.tipo_trabajo]) porTipo[p.tipo_trabajo] = { total: 0, sumReal: 0, sumEsp: 0 };
              porTipo[p.tipo_trabajo].total++;
              porTipo[p.tipo_trabajo].sumReal += parseFloat(p.avance_real || 0);
              porTipo[p.tipo_trabajo].sumEsp  += parseFloat(p.avance_esperado || 0);
            });
            const dataTipo = Object.entries(porTipo).map(([tipo, d]) => ({
              tipo: tipo.length > 20 ? tipo.slice(0,20)+"…" : tipo,
              planes: d.total,
              avgReal: Math.round(d.sumReal / d.total),
              avgEsp:  Math.round(d.sumEsp  / d.total),
            }));

            // Avance por responsable
            const porResp = {};
            planes.forEach(p => {
              const r = p.responsable || "Sin responsable";
              if (!porResp[r]) porResp[r] = { total:0, sumReal:0, completados:0, pendientes:0, enProceso:0 };
              porResp[r].total++;
              porResp[r].sumReal += parseFloat(p.avance_real||0);
              if (p.estado==="Completado") porResp[r].completados++;
              if (p.estado==="Pendiente")  porResp[r].pendientes++;
              if (p.estado==="En proceso") porResp[r].enProceso++;
            });
            const dataResp = Object.entries(porResp).map(([jefe, d]) => ({
              jefe: jefe.split(" ")[0],
              total: d.total, completados: d.completados, pendientes: d.pendientes, enProceso: d.enProceso,
              avgReal: Math.round(d.sumReal / d.total),
            }));

            return (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

                {/* Gráfica 1: Avance real vs esperado por plan */}
                <div style={cardStyle}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                    <div style={{ width: "3px", height: "16px", background: C.red, borderRadius: "2px" }} />
                    <span style={{ fontSize: "13px", fontWeight: "700", color: C.navy }}>Avance real vs esperado por plan</span>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={dataAvance} margin={{ top: 8, right: 20, left: 0, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="nombre" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} />
                      <YAxis domain={[0,100]} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={v => `${v}%`} />
                      <Legend />
                      <Line type="monotone" dataKey="real"     name="Avance Real"     stroke={C.red}     strokeWidth={2.5} dot={{ r:4 }} />
                      <Line type="monotone" dataKey="esperado" name="Avance Esperado"  stroke="#94a3b8"   strokeWidth={2}   strokeDasharray="5 5" dot={{ r:3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Gráfica 2: Avance promedio por tipo de trabajo */}
                <div style={cardStyle}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                    <div style={{ width: "3px", height: "16px", background: C.red, borderRadius: "2px" }} />
                    <span style={{ fontSize: "13px", fontWeight: "700", color: C.navy }}>Avance promedio por tipo de trabajo</span>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={dataTipo} margin={{ top: 8, right: 20, left: 0, bottom: 50 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="tipo" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} />
                      <YAxis domain={[0,100]} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={v => `${v}%`} />
                      <Legend />
                      <Line type="monotone" dataKey="avgReal" name="Avance Real Prom." stroke={C.red}   strokeWidth={2.5} dot={{ r:4 }} />
                      <Line type="monotone" dataKey="avgEsp"  name="Esperado Prom."   stroke="#94a3b8" strokeWidth={2}   strokeDasharray="5 5" dot={{ r:3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Tabla dinámica: estado por responsable */}
                <div style={cardStyle}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                    <div style={{ width: "3px", height: "16px", background: C.red, borderRadius: "2px" }} />
                    <span style={{ fontSize: "13px", fontWeight: "700", color: C.navy }}>Resumen por jefe de zona</span>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                      <thead>
                        <tr style={{ background: C.navy }}>
                          {["Jefe","Total","Pendientes","En proceso","Completados","Avance prom.","Estado"].map((h,i) => (
                            <th key={i} style={{ padding: "8px 10px", color: "white", fontWeight: "600", textAlign: i===0?"left":"center", fontSize: "11px", whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dataResp.map((r, i) => {
                          const pct = r.avgReal;
                          const badge = pct>=70?{bg:"#dcfce7",c:"#15803d",l:"Al día"}:pct>=40?{bg:"#fef9c3",c:"#92400e",l:"En riesgo"}:{bg:"#fee2e2",c:"#dc2626",l:"Atrasado"};
                          return (
                            <tr key={i} style={{ background: i%2===0?"#fff":"#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ padding: "8px 10px", fontWeight: "600", color: C.navy }}>{r.jefe}</td>
                              <td style={{ padding: "8px 10px", textAlign: "center", fontWeight: "700", color: C.navy }}>{r.total}</td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                {r.pendientes > 0 ? <span style={{ background: "#fef9c3", color: "#92400e", borderRadius: "4px", padding: "1px 8px", fontWeight: "700" }}>{r.pendientes}</span> : <span style={{ color: "#cbd5e1" }}>—</span>}
                              </td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                {r.enProceso > 0 ? <span style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: "4px", padding: "1px 8px", fontWeight: "700" }}>{r.enProceso}</span> : <span style={{ color: "#cbd5e1" }}>—</span>}
                              </td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                {r.completados > 0 ? <span style={{ background: "#dcfce7", color: "#15803d", borderRadius: "4px", padding: "1px 8px", fontWeight: "700" }}>{r.completados}</span> : <span style={{ color: "#cbd5e1" }}>—</span>}
                              </td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <div style={{ flex: 1, height: "6px", background: "#e2e8f0", borderRadius: "99px", overflow: "hidden" }}>
                                    <div style={{ height: "100%", width: `${pct}%`, background: badge.c, borderRadius: "99px" }} />
                                  </div>
                                  <span style={{ fontWeight: "700", color: badge.c, minWidth: "32px", textAlign: "right" }}>{pct}%</span>
                                </div>
                              </td>
                              <td style={{ padding: "8px 10px", textAlign: "center" }}>
                                <span style={{ background: badge.bg, color: badge.c, borderRadius: "20px", padding: "2px 10px", fontSize: "11px", fontWeight: "700" }}>{badge.l}</span>
                              </td>
                            </tr>
                          );
                        })}
                        {/* Fila totales */}
                        <tr style={{ background: "#0f172a" }}>
                          <td style={{ padding: "8px 10px", color: "white", fontWeight: "700" }}>TOTAL</td>
                          <td style={{ padding: "8px 10px", textAlign: "center", color: "white", fontWeight: "700" }}>{planes.length}</td>
                          <td style={{ padding: "8px 10px", textAlign: "center", color: "#fde68a", fontWeight: "700" }}>{planes.filter(p=>p.estado==="Pendiente").length}</td>
                          <td style={{ padding: "8px 10px", textAlign: "center", color: "#93c5fd", fontWeight: "700" }}>{planes.filter(p=>p.estado==="En proceso").length}</td>
                          <td style={{ padding: "8px 10px", textAlign: "center", color: "#86efac", fontWeight: "700" }}>{planes.filter(p=>p.estado==="Completado").length}</td>
                          <td style={{ padding: "8px 10px", textAlign: "center", color: "white", fontWeight: "700" }}>
                            {planes.length > 0 ? Math.round(planes.reduce((s,p)=>s+parseFloat(p.avance_real||0),0)/planes.length) : 0}%
                          </td>
                          <td style={{ padding: "8px 10px" }} />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            );
          })()}

        </div>
      )}

      {/* ══ MODAL CREAR/EDITAR PLAN ══ */}
      {showModalPlan && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "white", borderRadius: "16px", padding: "28px 32px", width: "100%", maxWidth: "640px", margin: "20px", maxHeight: "92vh", overflowY: "auto" }}>

            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", paddingBottom: "14px", borderBottom: "1px solid #e2e8f0" }}>
              <h3 style={{ margin: 0, color: C.navy, fontSize: "17px", fontWeight: "700" }}>{editandoPlan ? "Editar Plan" : "Nuevo Plan de Trabajo"}</h3>
              <button onClick={() => { setShowModalPlan(false); setNodosForm({}); }} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: C.slate }}>×</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>

              {/* ── Sección 1: Identificación ── */}
              <div>
                <div style={{ fontSize: "10px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ width: "3px", height: "14px", background: C.red, borderRadius: "2px" }} />
                  Identificación del trabajo
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={labelStyle}>Tipo de Trabajo *</label>
                    <select value={formPlan.tipo_trabajo} onChange={e => setFormPlan({...formPlan, tipo_trabajo: e.target.value})} style={{ ...inputStyle, cursor: "pointer" }}>
                      <option value="">Seleccionar...</option>
                      {TIPOS_TRABAJO.map((t, i) => <option key={i} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Descripción del Trabajo</label>
                    <textarea value={formPlan.descripcion} onChange={e => setFormPlan({...formPlan, descripcion: e.target.value})} rows={2} placeholder="Describe el alcance y objetivo..." style={{ ...inputStyle, resize: "vertical" }} />
                  </div>
                </div>
              </div>

              {/* ── Sección 2: Responsable y ubicación ── */}
              <div>
                <div style={{ fontSize: "10px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ width: "3px", height: "14px", background: C.red, borderRadius: "2px" }} />
                  Responsable y ubicación
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={labelStyle}>Jefe Responsable *</label>
                    <select value={formPlan.responsable} onChange={e => setFormPlan({...formPlan, responsable: e.target.value})} style={{ ...inputStyle, cursor: "pointer" }}>
                      <option value="">Seleccionar...</option>
                      {jefesDisponibles.map((j, i) => <option key={i} value={j}>{j}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Aliado</label>
                    <select value={formPlan.aliado} onChange={e => setFormPlan({...formPlan, aliado: e.target.value})} style={{ ...inputStyle, cursor: "pointer" }}>
                      <option value="">Seleccionar...</option>
                      {aliadosDisponibles.map((a, i) => <option key={i} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Ciudad</label>
                    <select value={formPlan.ciudad} onChange={e => setFormPlan({...formPlan, ciudad: e.target.value})} style={{ ...inputStyle, cursor: "pointer" }}>
                      <option value="">Seleccionar...</option>
                      {ciudadesDisponibles.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* ── Sección 3: Nodos ── */}
              <div>
                <div style={{ fontSize: "10px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ width: "3px", height: "14px", background: C.red, borderRadius: "2px" }} />
                  Nodos del plan
                </div>

                {/* Control desplegable multi-nodo */}
                <div style={{ position: "relative", marginBottom: "10px" }}>
                  <div
                    onClick={() => setDDNodosForm(v => !v)}
                    style={{ border: "1.5px solid #e2e8f0", borderRadius: "8px", background: "#f8fafc", padding: "8px 11px", cursor: "pointer", minHeight: "40px", display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                    {Object.keys(nodosForm).length === 0
                      ? <span style={{ fontSize: "13px", color: "#94a3b8" }}>Busca y selecciona uno o varios nodos...</span>
                      : Object.keys(nodosForm).map(id => (
                          <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#dbeafe", color: "#1d4ed8", borderRadius: "20px", padding: "2px 8px", fontSize: "12px", fontWeight: "600" }}>
                            {id}
                            <button onClick={e => { e.stopPropagation(); quitarNodoForm(id); }} style={{ background: "none", border: "none", color: "#1d4ed8", cursor: "pointer", fontSize: "14px", lineHeight: 1, padding: 0 }}>×</button>
                          </span>
                        ))
                    }
                    <span style={{ marginLeft: "auto", fontSize: "10px", color: C.slate, flexShrink: 0 }}>▼</span>
                  </div>

                  {dropdownNodosForm && (
                    <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "white", border: "1.5px solid #e2e8f0", borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 400 }}>
                      <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
                        <input
                          type="text"
                          value={busquedaNodoForm}
                          onChange={e => setBusqNodoForm(e.target.value)}
                          onClick={e => e.stopPropagation()}
                          placeholder="Escribe sigla o nombre del nodo..."
                          style={{ ...inputStyle, margin: 0, fontSize: "12px" }}
                          autoFocus
                        />
                      </div>
                      <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                        {nodosDisponibles
                          .filter((n, idx, arr) => arr.findIndex(x => x.id_nodo === n.id_nodo) === idx) // únicos por id
                          .filter(n => !busquedaNodoForm || n.id_nodo.toLowerCase().includes(busquedaNodoForm.toLowerCase()) || (n.NOMBRE_NODO||"").toLowerCase().includes(busquedaNodoForm.toLowerCase()))
                          .slice(0, 80)
                          .map((n, i) => {
                            const sel = !!nodosForm[n.id_nodo];
                            return (
                              <div key={i} onClick={() => toggleNodoForm(n)}
                                style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px", cursor: "pointer", fontSize: "13px", background: sel ? "#eff6ff" : i % 2 === 0 ? "white" : "#f8fafc", borderBottom: "0.5px solid #f1f5f9" }}>
                                <div style={{ width: "15px", height: "15px", border: `1.5px solid ${sel ? "#1d4ed8" : "#cbd5e1"}`, borderRadius: "3px", background: sel ? "#1d4ed8" : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "10px", color: "white" }}>
                                  {sel && "✓"}
                                </div>
                                <span style={{ fontWeight: "600", minWidth: "70px", color: C.navy }}>{n.id_nodo}</span>
                                <span style={{ fontSize: "12px", color: C.slate }}>{n.NOMBRE_NODO || ""}</span>
                                {n.categoria && (
                                  <span style={{ marginLeft: "auto", background: n.categoria === "N0" ? "#16a34a" : n.categoria === "N1" ? "#22c55e" : n.categoria === "N2" ? "#84cc16" : n.categoria === "N3" ? "#eab308" : n.categoria === "N4" ? "#f97316" : "#ef4444", color: "white", borderRadius: "12px", padding: "1px 8px", fontSize: "11px", fontWeight: "700" }}>{n.categoria}</span>
                                )}
                              </div>
                            );
                          })}
                      </div>
                      <div style={{ padding: "8px 12px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: "11px", color: C.slate }}>{Object.keys(nodosForm).length} nodo(s) seleccionado(s)</span>
                        <button onClick={() => setDDNodosForm(false)} style={{ ...btnGris, padding: "4px 12px", fontSize: "12px" }}>Cerrar</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Tabla de nodos seleccionados con mes y categoría */}
                {Object.keys(nodosForm).length > 0 && (
                  <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Nodo</th>
                          <th style={thStyle}>Nombre</th>
                          <th style={{ ...thStyle, textAlign: "center" }}>Mes Ofensor</th>
                          <th style={{ ...thStyle, textAlign: "center" }}>Categoría</th>
                          <th style={{ ...thStyle, textAlign: "center", fontSize: "10px" }}>Tipo corte</th>
                          <th style={{ ...thStyle, textAlign: "center" }}>Quitar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(nodosForm).map(([id, info], i) => {
                          const mesesNodo = Array.isArray(mesesPorNodo[id]) ? mesesPorNodo[id] : [];
                          const mesActual = mesesNodo.length > 0 ? mesesNodo[0].mes : "";
                          const esMesActual = info.mes === mesActual;
                          const catColor = info.categoria === "N0" ? "#16a34a" : info.categoria === "N1" ? "#22c55e" : info.categoria === "N2" ? "#84cc16" : info.categoria === "N3" ? "#eab308" : info.categoria === "N4" ? "#f97316" : info.categoria === "N5" ? "#ef4444" : "#991b1b";
                          return (
                            <tr key={id} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                              <td style={{ ...tdStyle, fontWeight: "700", color: C.navy }}>{id}</td>
                              <td style={{ ...tdStyle, color: C.slate, fontSize: "11px" }}>{info.nombre}</td>
                              <td style={{ ...tdStyle, textAlign: "center" }}>
                                <select
                                  value={info.mes}
                                  onChange={e => cambiarMesNodoForm(id, e.target.value)}
                                  style={{ padding: "4px 8px", border: "0.5px solid #e2e8f0", borderRadius: "6px", fontSize: "12px", background: "#f8fafc", color: C.navy, cursor: "pointer" }}>
                                  {mesesNodo.map((m, mi) => (
                                    <option key={mi} value={m.mes}>{formatMes(m.mes + "-01")} {mi === 0 ? "(actual)" : ""}</option>
                                  ))}
                                </select>
                              </td>
                              <td style={{ ...tdStyle, textAlign: "center" }}>
                                {info.categoria
                                  ? <span style={{ background: catColor, color: "white", borderRadius: "12px", padding: "1px 8px", fontSize: "11px", fontWeight: "700" }}>{info.categoria}</span>
                                  : <span style={{ color: C.light }}>—</span>}
                              </td>
                              <td style={{ ...tdStyle, textAlign: "center" }}>
                                {esMesActual
                                  ? <span style={{ background: "#fef9c3", color: "#92400e", borderRadius: "4px", padding: "1px 6px", fontSize: "10px", fontWeight: "600" }}>Más reciente</span>
                                  : <span style={{ background: "#f0fdf4", color: "#15803d", borderRadius: "4px", padding: "1px 6px", fontSize: "10px", fontWeight: "600" }}>Último día</span>}
                              </td>
                              <td style={{ ...tdStyle, textAlign: "center" }}>
                                <button onClick={() => quitarNodoForm(id)} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: "6px", padding: "3px 8px", fontSize: "11px", cursor: "pointer" }}>Quitar</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* ── Sección 4: OT, CHG, Fechas y Estado OT ── */}
              <div>
                <div style={{ fontSize: "10px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ width: "3px", height: "14px", background: C.red, borderRadius: "2px" }} />
                  Orden de trabajo y fechas
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                  <div>
                    <label style={labelStyle}>OT *</label>
                    <input type="text" value={formPlan.orden_trabajo} onChange={e => setFormPlan({...formPlan, orden_trabajo: e.target.value})} placeholder="OT292929" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>CHG</label>
                    <input type="text" value={formPlan.chg} onChange={e => setFormPlan({...formPlan, chg: e.target.value})} placeholder="CHG292929" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Fecha Inicio *</label>
                    <input type="date" value={formPlan.fecha_inicio} onChange={e => setFormPlan({...formPlan, fecha_inicio: e.target.value})} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Fecha Fin *</label>
                    <input type="date" value={formPlan.fecha_fin} onChange={e => setFormPlan({...formPlan, fecha_fin: e.target.value})} style={inputStyle} />
                  </div>
                </div>
                <div style={{ maxWidth: "300px" }}>
                  <label style={labelStyle}>Estado Orden de Trabajo *</label>
                  <select value={formPlan.estado_ot} onChange={e => setFormPlan({...formPlan, estado_ot: e.target.value})} style={{ ...inputStyle, cursor: "pointer" }}>
                    {ESTADOS.map((e, i) => <option key={i} value={e}>{e}</option>)}
                  </select>
                </div>
              </div>

              {/* ── Sección 5: Avances (solo lectura) ── */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "12px 14px", border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: "10px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "4px" }}>Avance Esperado</div>
                  <div style={{ fontSize: "20px", fontWeight: "700", color: C.slate }}>—%</div>
                  <div style={{ fontSize: "11px", color: C.light, marginTop: "2px" }}>Se gestiona desde la pestaña Avances</div>
                </div>
                <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "12px 14px", border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: "10px", fontWeight: "700", color: C.slate, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "4px" }}>Avance Real</div>
                  <div style={{ fontSize: "20px", fontWeight: "700", color: "#15803d" }}>0%</div>
                  <div style={{ fontSize: "11px", color: C.light, marginTop: "2px" }}>Se calcula automáticamente</div>
                </div>
              </div>

              {/* Botones */}
              <div style={{ display: "flex", gap: "10px", paddingTop: "8px", borderTop: "1px solid #e2e8f0" }}>
                <button onClick={() => { setShowModalPlan(false); setNodosForm({}); }} style={{ ...btnGris, flex: 1 }}>Cancelar</button>
                <button
                  onClick={guardarPlan}
                  disabled={loading || !formPlan.tipo_trabajo || !formPlan.responsable || !formPlan.orden_trabajo}
                  style={{ ...btnRojo, flex: 2, opacity: (!formPlan.tipo_trabajo || !formPlan.responsable || !formPlan.orden_trabajo) ? 0.5 : 1 }}>
                  {loading ? "Guardando..." : editandoPlan ? "Guardar Cambios" : "Crear Plan"}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL AGREGAR NODOS ══ */}
      {showModalNodos && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "white", borderRadius: "16px", padding: "32px", width: "100%", maxWidth: "700px", margin: "20px", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h3 style={{ margin: 0, color: C.navy, fontSize: "18px", fontWeight: "700" }}>Agregar Nodos al Plan</h3>
              <button onClick={() => { setShowModalNodos(false); setNodosSelec([]); setBusquedaNodo(""); setFiltroJefeNodo(""); setFiltroAliadoNodo(""); setFiltroCiudadNodo(""); setFiltroCategoriaNodo(""); }} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: C.slate }}>×</button>
            </div>
            {/* Fila 1: Busqueda + Mes */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "8px" }}>
              <input type="text" placeholder="Buscar nodo por ID o nombre..." value={busquedaNodo} onChange={e => setBusquedaNodo(e.target.value)}
                style={{ ...inputStyle, margin: 0 }} />
              <select value={mesSeleccionado} onChange={e => { setMesSelec(e.target.value); setNodosSelec([]); setFiltroJefeNodo(""); setFiltroAliadoNodo(""); setFiltroCiudadNodo(""); setFiltroCategoriaNodo(""); }}
                style={{ ...inputStyle, margin: 0, cursor: "pointer" }}>
                <option value="">Todos los meses</option>
                {mesesDisponibles.map((m, i) => (
                  <option key={i} value={m}>{formatMes(m)}</option>
                ))}
              </select>
            </div>
            {/* Fila 2: Filtros adicionales */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "8px", marginBottom: "10px" }}>
              <select value={filtroJefeNodo} onChange={e => setFiltroJefeNodo(e.target.value)} style={{ ...inputStyle, margin: 0, cursor: "pointer", fontSize: "11px" }}>
                <option value="">Jefe técnico</option>
                {optsJefeNodo.map((j,i) => <option key={i} value={j}>{j}</option>)}
              </select>
              <select value={filtroAliadoNodo} onChange={e => setFiltroAliadoNodo(e.target.value)} style={{ ...inputStyle, margin: 0, cursor: "pointer", fontSize: "11px" }}>
                <option value="">Aliado</option>
                {optsAliadoNodo.map((a,i) => <option key={i} value={a}>{a}</option>)}
              </select>
              <select value={filtroCiudadNodo} onChange={e => setFiltroCiudadNodo(e.target.value)} style={{ ...inputStyle, margin: 0, cursor: "pointer", fontSize: "11px" }}>
                <option value="">Ciudad</option>
                {optsCiudadNodo.map((c,i) => <option key={i} value={c}>{c}</option>)}
              </select>
              <select value={filtroCategoriaNodo} onChange={e => setFiltroCategoriaNodo(e.target.value)} style={{ ...inputStyle, margin: 0, cursor: "pointer", fontSize: "11px" }}>
                <option value="">Categoría</option>
                {optsCatNodo.map((c,i) => <option key={i} value={c}>{c}</option>)}
              </select>
            </div>
            {/* Totales */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", color: C.slate, marginBottom: "8px" }}>
              <span>{nodosSeleccionados.length} seleccionados — Haz clic para seleccionar/deseleccionar</span>
              <span style={{ fontWeight: "600", color: C.navy }}>
                Mostrando <strong>{nodosFiltrados.length}</strong> de <strong>{nodosDelMes.length}</strong> nodos del corte
              </span>
            </div>
            <div style={{ maxHeight: "350px", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead style={{ position: "sticky", top: 0 }}>
                  <tr>
                    <th style={{ ...thStyle, width: "40px", textAlign: "center" }}></th>
                    <th style={thStyle}>Nodo</th>
                    <th style={thStyle}>Nombre</th>
                    <th style={thStyle}>Jefe Técnico</th>
                    <th style={thStyle}>Aliado</th>
                    <th style={thStyle}>Ciudad</th>
                    <th style={{ ...thStyle, textAlign: "center" }}>Categoría</th>
                    <th style={{ ...thStyle, textAlign: "center" }}>Mes</th>
                  </tr>
                </thead>
                <tbody>
                  {nodosFiltrados.slice(0, 100).map((n, i) => {
                    const key = `${n.id_nodo}||${formatFecha(n.fecha_corte)}`;
                    const seleccionado = nodosSeleccionados.some(s => `${s.id_nodo}||${formatFecha(s.fecha_corte)}` === key);
                    return (
                      <tr key={i} onClick={() => setNodosSelec(prev => seleccionado ? prev.filter(s => `${s.id_nodo}||${formatFecha(s.fecha_corte)}` !== key) : [...prev, n])}
                        style={{ background: seleccionado ? "#dbeafe" : i % 2 === 0 ? "#fff" : "#f8fafc", cursor: "pointer" }}>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <input type="checkbox" checked={seleccionado} readOnly />
                        </td>
                        <td style={{ ...tdStyle, fontWeight: "600" }}>{n.id_nodo}</td>
                        <td style={tdStyle}>{n.NOMBRE_NODO || "—"}</td>
                        <td style={{ ...tdStyle, fontSize: "11px" }}>{n.nombre_jefe || "—"}</td>
                        <td style={{ ...tdStyle, fontSize: "11px" }}>{n.aliado || "—"}</td>
                        <td style={tdStyle}>{n.ciudad || "—"}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <span style={{ background: n.categoria === "N0" ? "#16a34a" : n.categoria === "N1" ? "#22c55e" : n.categoria === "N2" ? "#84cc16" : n.categoria === "N3" ? "#eab308" : n.categoria === "N4" ? "#f97316" : n.categoria === "N5" ? "#ef4444" : "#991b1b", color: "white", borderRadius: "12px", padding: "1px 8px", fontSize: "11px", fontWeight: "700" }}>{n.categoria || "—"}</span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{formatMes(n.mes_anio)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
              <button onClick={() => { setShowModalNodos(false); setNodosSelec([]); setBusquedaNodo(""); setFiltroJefeNodo(""); setFiltroAliadoNodo(""); setFiltroCiudadNodo(""); setFiltroCategoriaNodo(""); }} style={{ ...btnGris, flex: 1 }}>Cancelar</button>
              <button onClick={agregarNodos} disabled={nodosSeleccionados.length === 0} style={{ ...btnRojo, flex: 1, opacity: nodosSeleccionados.length === 0 ? 0.5 : 1 }}>
                Agregar {nodosSeleccionados.length > 0 ? `(${nodosSeleccionados.length})` : ""}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL REGISTRAR AVANCE ══ */}
      {showModalAvance && planSeleccionado && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "white", borderRadius: "16px", padding: "32px", width: "100%", maxWidth: "420px", margin: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ margin: 0, color: C.navy, fontSize: "18px", fontWeight: "700" }}>Registrar Avance</h3>
              <button onClick={() => setShowModalAvance(false)} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: C.slate }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "10px 12px", fontSize: "12px", color: C.slate }}>
                <div style={{ fontWeight: "700", color: C.navy, marginBottom: "2px" }}>{planSeleccionado.nombre_plan}</div>
                <div>{planSeleccionado.tipo_trabajo}</div>
              </div>
              <div>
                <label style={labelStyle}>Avance Real (%) — Avance Esperado: {parseFloat(planSeleccionado.avance_esperado || 0).toFixed(1)}%</label>
                <input type="number" min="0" max="100" step="0.1" value={formAvance.avance_real} onChange={e => setFormAvance({...formAvance, avance_real: e.target.value})} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Observacion del Avance</label>
                <textarea value={formAvance.observacion} onChange={e => setFormAvance({...formAvance, observacion: e.target.value})} rows={4} placeholder="Describe el avance realizado..." style={{ ...inputStyle, resize: "vertical" }} />
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                <button onClick={() => setShowModalAvance(false)} style={{ ...btnGris, flex: 1 }}>Cancelar</button>
                <button onClick={registrarAvance} disabled={loading} style={{ ...btnRojo, flex: 1 }}>
                  {loading ? "Guardando..." : "Registrar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ POPUP NODOS DEL PLAN ══ */}
      {popupNodosPlan && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}
          onClick={() => setPopupNodosPlan(null)}>
          <div style={{ background: "white", borderRadius: "16px", padding: "28px", width: "100%", maxWidth: "800px", margin: "20px", maxHeight: "85vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div>
                <h3 style={{ margin: 0, color: C.navy, fontSize: "16px", fontWeight: "700" }}>Nodos del Plan</h3>
                <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: C.slate }}>{popupNodosPlan.plan.nombre_plan}</p>
              </div>
              <button onClick={() => setPopupNodosPlan(null)} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: C.slate, lineHeight: 1 }}>×</button>
            </div>

            {/* Info resumen del plan */}
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px" }}>
              {[
                { label: "Tipo", value: popupNodosPlan.plan.tipo_trabajo },
                { label: "Responsable", value: popupNodosPlan.plan.responsable || "—" },
                { label: "Estado", value: popupNodosPlan.plan.estado },
                { label: "Avance Real", value: `${parseFloat(popupNodosPlan.plan.avance_real).toFixed(1)}%` },
                { label: "Total Nodos", value: popupNodosPlan.plan.total_nodos || 0 },
              ].map((item, i) => (
                <div key={i} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "8px 14px" }}>
                  <div style={{ fontSize: "10px", color: C.slate, fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.05em" }}>{item.label}</div>
                  <div style={{ fontSize: "13px", fontWeight: "700", color: C.navy }}>{item.value}</div>
                </div>
              ))}
            </div>

            {loadingPopupNodos ? (
              <div style={{ textAlign: "center", padding: "30px", color: C.slate, fontSize: "13px" }}>Cargando nodos...</div>
            ) : popupNodosPlan.nodos.length === 0 ? (
              <div style={{ textAlign: "center", padding: "30px", color: C.slate, fontSize: "13px" }}>Este plan no tiene nodos asociados.</div>
            ) : (
              <>
                {/* Buscador dentro del popup */}
                {busquedaNodoInverso && (
                  <div style={{ background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: "8px", padding: "8px 12px", marginBottom: "10px", fontSize: "12px", color: "#1d4ed8", fontWeight: "600" }}>
                    🔍 Filtrando por: "{busquedaNodoInverso}" —{" "}
                    {popupNodosPlan.nodos.filter(n => n.nodo?.toUpperCase().includes(busquedaNodoInverso) || n.NOMBRE_NODO?.toUpperCase().includes(busquedaNodoInverso)).length} coincidencias
                  </div>
                )}
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Nodo</th>
                        <th style={thStyle}>Nombre</th>
                        <th style={thStyle}>Ciudad</th>
                        <th style={{ ...thStyle, textAlign: "center" }}>Categoría</th>
                        <th style={thStyle}>Jefe Técnico</th>
                        <th style={thStyle}>Aliado</th>
                        <th style={thStyle}>Causa Falla</th>
                        <th style={{ ...thStyle, textAlign: "center" }}>Mes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {popupNodosPlan.nodos
                        .filter(n => !busquedaNodoInverso || n.nodo?.toUpperCase().includes(busquedaNodoInverso) || n.NOMBRE_NODO?.toUpperCase().includes(busquedaNodoInverso))
                        .map((n, i) => {
                          const catColor = n.categoria === "N0" ? "#16a34a" : n.categoria === "N1" ? "#22c55e" : n.categoria === "N2" ? "#84cc16" : n.categoria === "N3" ? "#eab308" : n.categoria === "N4" ? "#f97316" : n.categoria === "N5" ? "#ef4444" : "#991b1b";
                          const coincide = busquedaNodoInverso && (n.nodo?.toUpperCase().includes(busquedaNodoInverso) || n.NOMBRE_NODO?.toUpperCase().includes(busquedaNodoInverso));
                          return (
                            <tr key={i} style={{ background: coincide ? "#fefce8" : i % 2 === 0 ? "#fff" : "#f8fafc", border: coincide ? "2px solid #fde047" : "none" }}>
                              <td style={{ ...tdStyle, fontWeight: "700", color: C.navy }}>{n.nodo}</td>
                              <td style={tdStyle}>{n.NOMBRE_NODO || "—"}</td>
                              <td style={tdStyle}>{n.ciudad || "—"}</td>
                              <td style={{ ...tdStyle, textAlign: "center" }}>
                                <span style={{ background: catColor, color: "white", borderRadius: "12px", padding: "1px 8px", fontSize: "11px", fontWeight: "700" }}>{n.categoria || "—"}</span>
                              </td>
                              <td style={{ ...tdStyle, fontSize: "11px" }}>{n.nombre_jefe || "—"}</td>
                              <td style={{ ...tdStyle, fontSize: "11px" }}>{n.aliado || "—"}</td>
                              <td style={{ ...tdStyle, fontSize: "11px" }}>{n.causa_falla || "—"}</td>
                              <td style={{ ...tdStyle, textAlign: "center" }}>{formatMes(n.fecha_corte)}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px" }}>
              <button onClick={() => { abrirDetalle(popupNodosPlan.plan); setPopupNodosPlan(null); }}
                style={{ ...btnRojo }}>Ver plan completo →</button>
              <button onClick={() => setPopupNodosPlan(null)} style={btnGris}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

    </div>
    </div>
  );
}